import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { 
  PlusIcon, 
  CalendarIcon, 
  MapPinIcon,
  UserIcon,
  LanguageIcon,
  TruckIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  InformationCircleIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import InterpreterSearchAnimation from '../components/InterpreterSearchAnimation';
import googleMapsLoader from '../utils/googleMapsLoader';
import ServiceLocationSelector from '../components/ServiceLocationSelector';
import { navigateBack } from '../components/RoutePersistence';
import { createDatePasteHandler } from '../utils/dateUtils';
import { capitalizeName } from '../utils/nameUtils';
import { getCustomerPortalErrorMessage } from '../utils/apiErrorUtils';
import {
  interpretingJobServiceLocationOptional,
  shouldSkipLocationStep
} from '../utils/interpretingJobLocation';
import {
  fetchClaimantNameDuplicate,
  fetchClaimNumberDuplicate,
  showDuplicateAlert,
  requestDuplicateConfirm,
  runClaimantNameDuplicateCheck
} from '../utils/duplicateClaimChecks';
import DuplicateWarningModal from '../components/DuplicateWarningModal';
import AdjusterConfirmModal from '../components/AdjusterConfirmModal';
import { useViewExistingClaim } from '../hooks/useViewExistingClaim';
import {
  getDefaultReserveHours,
  getMinimumReserveHours,
  resolveReserveHours,
  resolveServiceTypeForReserveRules,
} from '../utils/reserveHoursUtils';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Utility function to prevent Enter key from submitting forms accidentally
const preventEnterSubmit = (e) => {
  if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA' && e.target.type !== 'submit') {
    e.preventDefault();
    return false;
  }
};

// Simplified service type options for user selection
const serviceTypeOptions = [
  { value: 'onsite', label: 'On-Site' },
  { value: 'video', label: 'Video Remote Interpretation (Legal)' },
  { value: 'video_medical', label: 'Video Remote Interpretation (Medical)' },
  { value: 'phone', label: 'Telehealth' },
  { value: 'phone_conference', label: 'Phone Conference' },
  { value: 'document', label: 'Document' }
];

// Appointment type categorization for mapping logic
const legalAppointmentTypes = [
  'deposition', 'deposition_prep', 'legal', 'recorded_statement', 'settlement_documents',
  'status_conference', 'trial', 'mandatory_settlement_agreement',
  'hearing', 'mandatory_settlement_conference', 'permanent_stationary'
];

const medicalLegalAppointmentTypes = ['ame', 'ime', 'qme', 'psych_qme', 'aoe_coe', 'psychological', 'permanent_stationary'];

const medicalStandardAppointmentTypes = [
  'acupuncture', 'cardiac_evaluation', 'chiropractor', 'consult_treat', 'consult_diagnostic_testing', 'consultation',
  'ct_scan', 'dentist', 'diagnostic_testing', 'ekg', 'emg', 'epidural_injection', 'evaluation',
  'follow_up', 'follow_up_lab_work', 'follow_up_pt', 'functional_capacity', 'hearing_loss',
  'hernia_consult', 'initial_appointment', 'injection', 'injection_steroid', 'internal_medicine',
  'laser_procedure', 'massage', 'medical', 'medical_clerance', 'medical_procedure', 'mri', 'occupational',
  'one_time_appointment', 'orthopaedic', 'pain_management', 'physical_therapy', 'physical_therapy_initial',
  'psychological', 'second_opinion', 'surgery', 'surgery_check_in', 'surgery_discharge',
  'surgery_post_op', 'surgery_pre_op', 'surgical_consult', 'vocational_evaluation', 'x_ray',
  'cognitive_behavioral_therapy', 'speech_therapy_evaluation', 'speech_therapy_follow_up'
];

// Helper function to determine appointment category
const getAppointmentCategory = (appointmentType) => {
  if (legalAppointmentTypes.includes(appointmentType)) return 'legal';
  if (medicalLegalAppointmentTypes.includes(appointmentType)) return 'medical-legal';
  if (medicalStandardAppointmentTypes.includes(appointmentType)) return 'medical-standard';
  return 'other';
};

// Helper function to map frontend selections to backend service_type_id
const mapToBackendServiceType = (frontendServiceType, appointmentType, interpreterTypeCode, formOptions) => {
  // For non-onsite service types, map directly
  if (frontendServiceType === 'video') {
    return formOptions.serviceTypes?.find(st => st.code === 'video')?.id;
  }
  if (frontendServiceType === 'video_medical') {
    return formOptions.serviceTypes?.find(st => st.code === 'video_medical')?.id;
  }
  if (frontendServiceType === 'phone') {
    return formOptions.serviceTypes?.find(st => st.code === 'phone')?.id;
  }
  if (frontendServiceType === 'phone_conference') {
    return formOptions.serviceTypes?.find(st => st.code === 'phone_conference')?.id;
  }
  if (frontendServiceType === 'document') {
    return formOptions.serviceTypes?.find(st => st.code === 'document')?.id;
  }
  
  // For onsite, determine based on appointment type and interpreter type
  if (frontendServiceType === 'onsite') {
    const category = getAppointmentCategory(appointmentType);
    
    if (category === 'legal') {
      // Legal appointments always map to 'legal' service type
      return formOptions.serviceTypes?.find(st => st.code === 'legal')?.id;
    }
    
    if (category === 'medical-legal') {
      // Medical Certified appointments always map to 'medical' service type
      return formOptions.serviceTypes?.find(st => st.code === 'medical')?.id;
    }
    
    if (category === 'medical-standard') {
      // Medical Standard depends on selected interpreter type
      if (interpreterTypeCode === 'qualified_standard') {
        return formOptions.serviceTypes?.find(st => st.code === 'medical_non_legal')?.id;
      }
      if (interpreterTypeCode === 'court_certified') {
        return formOptions.serviceTypes?.find(st => st.code === 'legal')?.id;
      }
      if (interpreterTypeCode === 'medical_certified') {
        return formOptions.serviceTypes?.find(st => st.code === 'medical')?.id;
      }
    }
  }
  
  return null;
};

/** Resolve backend service-type key for reserve-hour defaults (2h standard, 3h legal/video). */
const resolveReserveServiceTypeKey = (formData, formOptions = {}) => {
  if (formData.serviceType === 'video') return 'video';

  const category = formData.appointmentType
    ? getAppointmentCategory(formData.appointmentType)
    : null;
  // Legal appointment types map to legal service type even before interpreter type is chosen
  if (category === 'legal') return 'legal';

  const interpreterTypeCode = formData.interpreterType
    ? formOptions.interpreterTypes?.find(
        (it) => it.id?.toString() === formData.interpreterType?.toString()
      )?.code
    : null;
  const backendServiceTypeId = mapToBackendServiceType(
    formData.serviceType,
    formData.appointmentType,
    interpreterTypeCode,
    formOptions
  );
  return resolveServiceTypeForReserveRules(
    backendServiceTypeId,
    formOptions.serviceTypes || [],
    formData.serviceType
  );
};

// Appointment type options (matching admin portal)
const appointmentTypeOptions = [
  { value: 'acupuncture', label: 'Acupuncture' },
  { value: 'ame', label: 'AME' },
  { value: 'aoe_coe', label: 'AOE/COE' },
  { value: 'benefit_meeting', label: 'Benefit Meeting' },
  { value: 'cardiac_evaluation', label: 'Cardiac Evaluation' },
  { value: 'chiropractor', label: 'Chiropractor' },
  { value: 'consult_treat', label: 'Consult & Treat' },
  { value: 'consult_diagnostic_testing', label: 'Consult + Diagnostic Testing' },
  { value: 'consultation', label: 'Consultation' },
  { value: 'ct_scan', label: 'Ct Scan' },
  { value: 'dentist', label: 'Dentist' },
  { value: 'deposition', label: 'Deposition' },
  { value: 'deposition_prep', label: 'Deposition Prep' },
  { value: 'diagnostic_testing', label: 'Diagnostic Testing' },
  { value: 'document', label: 'Document' },
  { value: 'ekg', label: 'EKG' },
  { value: 'emg', label: 'EMG' },
  { value: 'employee_meeting', label: 'Employee Meeting' },
  { value: 'epidural_injection', label: 'Epidural Injection' },
  { value: 'equipment_explination', label: 'Equipment Explination' },
  { value: 'evaluation', label: 'Evaluation' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'follow_up_lab_work', label: 'Follow Up & Lab Work' },
  { value: 'follow_up_pt', label: 'Follow Up & PT' },
  { value: 'functional_capacity', label: 'Functional Capacity' },
  { value: 'hearing_loss', label: 'Hearing Loss' },
  { value: 'hernia_consult', label: 'Hernia Consult' },
  { value: 'ime', label: 'IME' },
  { value: 'initial_appointment', label: 'Initial Appointment' },
  { value: 'injection', label: 'Injection' },
  { value: 'injection_steroid', label: 'Injection Steroid' },
  { value: 'internal_medicine', label: 'Internal Medicine' },
  { value: 'interview', label: 'Interview' },
  { value: 'interview_witness', label: 'Interview Witness' },
  { value: 'laser_procedure', label: 'Laser Procedure' },
  { value: 'legal', label: 'Legal' },
  { value: 'massage', label: 'Massage' },
  { value: 'medical', label: 'Medical' },
  { value: 'medical_clerance', label: 'Medical Clerance' },
  { value: 'medical_procedure', label: 'Medical Procedure' },
  { value: 'mri', label: 'MRI' },
  { value: 'ncm_meeting', label: 'NCM Meeting' },
  { value: 'occupational', label: 'Occupational' },
  { value: 'one_time_appointment', label: 'One-Time Appointment' },
  { value: 'orthopaedic', label: 'Orthopaedic' },
  { value: 'pain_management', label: 'Pain Management' },
  { value: 'physical_therapy', label: 'Physical Therapy' },
  { value: 'physical_therapy_initial', label: 'Physical Therapy Initial' },
  { value: 'psychological', label: 'Psychological' },
  { value: 'psych_qme', label: 'Psych QME' },
  { value: 'qme', label: 'QME' },
  { value: 'recorded_statement', label: 'Recorded Statement' },
  { value: 'second_opinion', label: 'Second Opinion' },
  { value: 'settlement_documents', label: 'Settlement Documents' },
  { value: 'status_conference', label: 'Status Conference' },
  { value: 'surgery', label: 'Surgery' },
  { value: 'surgery_check_in', label: 'Surgery Check In' },
  { value: 'surgery_discharge', label: 'Surgery Discharge' },
  { value: 'surgery_post_op', label: 'Surgery Post-Op' },
  { value: 'surgery_pre_op', label: 'Surgery Pre-Op' },
  { value: 'surgical_consult', label: 'Surgical Consult' },
  { value: 'trial', label: 'Trial' },
  { value: 'vocational_evaluation', label: 'Vocational Evaluation' },
  { value: 'x_ray', label: 'X-Ray' },
  { value: 'mandatory_settlement_agreement', label: 'Mandatory Settlement Agreement' },
  { value: 'hearing', label: 'Hearing' },
  { value: 'mandatory_settlement_conference', label: 'Mandatory Settlement Conference' },
  { value: 'permanent_stationary', label: 'Permanent & Stationary' },
  { value: 'cognitive_behavioral_therapy', label: 'Cognitive behavioral therapy' },
  { value: 'speech_therapy_evaluation', label: 'Speech Therapy Evaluation' },
  { value: 'speech_therapy_follow_up', label: 'Speech Therapy Follow Up' },
  { value: 'other', label: 'Other' }
];

const caseTypeOptions = [
  { value: 'Workers Compensation', label: 'Workers Compensation' },
  { value: 'Personal Injury', label: 'Personal Injury' },
  { value: 'Medical Malpractice', label: 'Medical Malpractice' },
  { value: 'Auto Accident', label: 'Auto Accident' },
  { value: 'Slip and Fall', label: 'Slip and Fall' },
  { value: 'Product Liability', label: 'Product Liability' },
  { value: 'Other', label: 'Other' }
];

const initialNewClaimState = {
  claimantId: '',
  caseType: '',
  claimNumber: '',
  dateOfInjury: '',
  ctStartDate: '',
  ctEndDate: '',
  employer: '',
  diagnosis: '',
  contactClaimsHandlerId: '',
  adjustersAssistantId: '',
  nurseCaseManagerId: '',
  fieldCaseManagerId: ''
};

const initialNewClaimantState = {
  firstName: '',
  lastName: '',
  phone: '',
  dateOfBirth: '',
  language: '',
  customLanguage: '',
  gender: '',
  address: '',
  address_latitude: '',
  address_longitude: ''
};

// UI Components
const Input = ({ value, onChange, onBlur, placeholder, type = 'text', className = '', label, required = false, disabled = false, readOnly = false, min, onClear }) => {
  const showClearButton = value && !disabled && !readOnly && onClear;
  
  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          type={type}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onPaste={type === 'date' ? createDatePasteHandler(onChange) : undefined}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          readOnly={readOnly}
          min={min}
          className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${disabled || readOnly ? 'bg-gray-50 text-gray-600 cursor-not-allowed' : ''} ${showClearButton ? 'pr-10' : ''}`}
        />
        {showClearButton && (
          <button
            type="button"
            onClick={() => onClear()}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
};

const SearchableSelect = ({ 
  label, 
  value, 
  onChange, 
  options = [], 
  placeholder = "Search...", 
  className = '', 
  required = false, 
  disabled = false,
  onClear,
  onEmptyResultsChange,
  onSearchTermChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    if (typeof onEmptyResultsChange === 'function') {
      const noResults = searchTerm.trim().length > 0 && filteredOptions.length === 0;
      onEmptyResultsChange(noResults, searchTerm);
    }
  }, [searchTerm, filteredOptions.length, onEmptyResultsChange]);

  // Set search term when value changes (for external updates)
  useEffect(() => {
    if (value) {
      const selectedOption = options.find(option => option.value === value);
      if (selectedOption) {
        const labelValue = selectedOption.label || '';
        setSearchTerm(labelValue);
        if (onSearchTermChange) {
          onSearchTermChange(labelValue);
        }
      }
    } else {
      setSearchTerm('');
      if (onSearchTermChange) {
        onSearchTermChange('');
      }
    }
  }, [value]);

  // When options update after value already selected, refresh display label
  useEffect(() => {
    if (!value) return;
    const selectedOption = options.find(option => option.value === value);
    if (selectedOption) {
      const labelValue = selectedOption.label || '';
      setSearchTerm(labelValue);
      if (onSearchTermChange) {
        onSearchTermChange(labelValue);
      }
    }
  }, [options, value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (option) => {
    if (option && option.value !== undefined) {
      onChange({ target: { value: option.value } });
      const labelValue = option.label || '';
      setSearchTerm(labelValue);
      if (onSearchTermChange) {
        onSearchTermChange(labelValue);
      }
      setIsOpen(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    if (onSearchTermChange) {
      onSearchTermChange(newValue);
    }
    setIsOpen(true);
  };

  const handleClear = () => {
    setSearchTerm('');
    onChange({ target: { value: '' } });
    if (onClear) {
      onClear();
    }
    if (onSearchTermChange) {
      onSearchTermChange('');
    }
  };

  const showClearButton = value && !disabled;

  return (
    <div className={`relative searchable-select ${className}`} ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${showClearButton ? 'pr-16' : 'pr-10'} ${disabled ? 'bg-gray-50 text-gray-600 cursor-not-allowed' : ''}`}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          {showClearButton && (
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 mr-2"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 pointer-events-none" />
        </div>
        {isOpen && filteredOptions.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
            {filteredOptions.map((option, index) => (
              <div
                key={index}
                onClick={() => handleSelect(option)}
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Step Components
const StepIndicator = ({ currentStep, totalSteps, completedSteps, onStepClick }) => {
  const steps = [
    { number: 1, title: 'Job Type', icon: PlusIcon },
    { number: 2, title: 'Claim Info', icon: UserIcon },
    { number: 3, title: 'Appointment Details', icon: CalendarIcon },
    { number: 4, title: 'Interpreter Requirements', icon: LanguageIcon },
    { number: 5, title: 'Location', icon: MapPinIcon }
  ];

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const isCompleted = completedSteps.has(step.number);
          const isCurrent = currentStep === step.number;
          const isClickable = step.number <= currentStep || completedSteps.has(step.number - 1);
          
          return (
            <div key={step.number} className="flex items-center">
              <div className="flex flex-col items-center">
                <button
                  onClick={() => isClickable && onStepClick(step.number)}
                  disabled={!isClickable}
                  className={`
                    w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors
                    ${isCompleted 
                      ? 'bg-green-600 text-white' 
                      : isCurrent 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-200 text-gray-600'
                    }
                    ${isClickable ? 'cursor-pointer hover:opacity-80' : 'cursor-not-allowed'}
                  `}
                >
                  {isCompleted ? (
                    <CheckCircleIcon className="h-5 w-5" />
                  ) : (
                    <step.icon className="h-5 w-5" />
                  )}
                </button>
                <span className={`mt-2 text-xs font-medium ${
                  isCurrent ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-500'
                }`}>
                  {step.title}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-4 ${
                  completedSteps.has(step.number) ? 'bg-green-600' : 'bg-gray-200'
                }`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Step1JobType = ({ 
  jobType, 
  setJobType, 
  setCurrentStep, 
  setServiceType, 
  showServiceTypeSelection, 
  setShowServiceTypeSelection 
}) => {
  const handleServiceTypeSelect = (selectedServiceType) => {
    setServiceType(selectedServiceType);
    setShowServiceTypeSelection(false);
    // Advance to step 2 after service type is selected
    setCurrentStep(2);
  };

  // Show service type selection if interpreting is selected
  if (showServiceTypeSelection && jobType === 'interpreting') {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Service Type</h2>
          <p className="text-gray-600">Choose how the interpreting service will be delivered</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* On-Site Card */}
          <div 
            onClick={() => handleServiceTypeSelect('onsite')}
            className="p-6 border-2 rounded-lg cursor-pointer transition-all border-gray-200 hover:border-blue-500 hover:bg-blue-50"
          >
            <div className="flex items-center mb-4">
              <MapPinIcon className="h-8 w-8 text-blue-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">On-Site</h3>
            </div>
            <p className="text-gray-600 mb-4">
              In-person interpretation at a physical location
            </p>
            <ul className="text-sm text-gray-500 space-y-1">
              <li>• Interpreter physically present</li>
              <li>• Medical, legal, or business settings</li>
              <li>• Face-to-face communication</li>
            </ul>
          </div>

          {/* Video Remote Interpretation (Legal) Card */}
          <div 
            onClick={() => handleServiceTypeSelect('video')}
            className="p-6 border-2 rounded-lg cursor-pointer transition-all border-gray-200 hover:border-purple-500 hover:bg-purple-50"
          >
            <div className="flex items-center mb-4">
              <svg className="h-8 w-8 text-purple-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <h3 className="text-xl font-semibold text-gray-900">Video Remote Interpretation (Legal)</h3>
            </div>
            <p className="text-gray-600 mb-4">
              Remote interpretation via video call for legal proceedings
            </p>
            <ul className="text-sm text-gray-500 space-y-1">
              <li>• Zoom or video conferencing</li>
              <li>• Legal appointments</li>
              <li>• Remote access convenience</li>
            </ul>
          </div>

          {/* Video Remote Interpretation (Medical) Card */}
          <div 
            onClick={() => handleServiceTypeSelect('video_medical')}
            className="p-6 border-2 rounded-lg cursor-pointer transition-all border-gray-200 hover:border-indigo-500 hover:bg-indigo-50"
          >
            <div className="flex items-center mb-4">
              <svg className="h-8 w-8 text-indigo-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <h3 className="text-xl font-semibold text-gray-900">Video Remote Interpretation (Medical)</h3>
            </div>
            <p className="text-gray-600 mb-4">
              Remote interpretation via video call for medical appointments
            </p>
            <ul className="text-sm text-gray-500 space-y-1">
              <li>• Zoom or video conferencing</li>
              <li>• Medical appointments</li>
              <li>• Remote access convenience</li>
            </ul>
          </div>

          {/* Telehealth Card */}
          <div 
            onClick={() => handleServiceTypeSelect('phone')}
            className="p-6 border-2 rounded-lg cursor-pointer transition-all border-gray-200 hover:border-green-500 hover:bg-green-50"
          >
            <div className="flex items-center mb-4">
              <svg className="h-8 w-8 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <h3 className="text-xl font-semibold text-gray-900">Telehealth</h3>
            </div>
            <p className="text-gray-600 mb-4">
              Phone-based interpretation services
            </p>
            <ul className="text-sm text-gray-500 space-y-1">
              <li>• Phone call interpretation</li>
              <li>• Audio-only communication</li>
              <li>• Quick and accessible</li>
            </ul>
          </div>

          {/* Phone Conference Card */}
          <div 
            onClick={() => handleServiceTypeSelect('phone_conference')}
            className="p-6 border-2 rounded-lg cursor-pointer transition-all border-gray-200 hover:border-teal-500 hover:bg-teal-50"
          >
            <div className="flex items-center mb-4">
              <svg className="h-8 w-8 text-teal-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <h3 className="text-xl font-semibold text-gray-900">Phone Conference</h3>
            </div>
            <p className="text-gray-600 mb-4">
              Non-medical phone call appointments
            </p>
            <ul className="text-sm text-gray-500 space-y-1">
              <li>• Phone call interpretation</li>
              <li>• Audio-only communication</li>
              <li>• Not a medical visit</li>
            </ul>
          </div>

          {/* Document Card */}
          <div 
            onClick={() => handleServiceTypeSelect('document')}
            className="p-6 border-2 rounded-lg cursor-pointer transition-all border-gray-200 hover:border-orange-500 hover:bg-orange-50"
          >
            <div className="flex items-center mb-4">
              <DocumentTextIcon className="h-8 w-8 text-orange-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Document</h3>
            </div>
            <p className="text-gray-600 mb-4">
              Written document translation services
            </p>
            <ul className="text-sm text-gray-500 space-y-1">
              <li>• Document translation</li>
              <li>• Written materials</li>
              <li>• Certified translations available</li>
            </ul>
          </div>
        </div>

        {/* Back Button */}
        <div className="text-center">
          <button
            type="button"
            onClick={() => setShowServiceTypeSelection(false)}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            ← Back to Job Type Selection
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Service Type</h2>
        <p className="text-gray-600">Choose the type of service you need</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div 
          className={`p-6 border-2 rounded-lg cursor-pointer transition-all ${
            jobType === 'interpreting' 
              ? 'border-blue-500 bg-blue-50' 
              : 'border-gray-200 hover:border-gray-300'
          }`}
          onClick={() => setJobType('interpreting')}
        >
          <div className="flex items-center mb-4">
            <LanguageIcon className="h-8 w-8 text-blue-600 mr-3" />
            <h3 className="text-xl font-semibold text-gray-900">Interpreting Service</h3>
          </div>
          <p className="text-gray-600 mb-4">
            Request an interpreter for medical appointments, legal proceedings, or other services requiring language assistance.
          </p>
          <ul className="text-sm text-gray-500 space-y-1">
            <li>• Medical appointments</li>
            <li>• Legal proceedings</li>
            <li>• Court hearings</li>
            <li>• Consultations</li>
          </ul>
        </div>

        <div 
          className={`p-6 border-2 rounded-lg cursor-pointer transition-all ${
            jobType === 'transportation' 
              ? 'border-green-500 bg-green-50' 
              : 'border-gray-200 hover:border-gray-300'
          }`}
          onClick={() => setJobType('transportation')}
        >
          <div className="flex items-center mb-4">
            <TruckIcon className="h-8 w-8 text-green-600 mr-3" />
            <h3 className="text-xl font-semibold text-gray-900">Transportation Service</h3>
          </div>
          <p className="text-gray-600 mb-4">
            Request transportation services to and from appointments or other destinations.
          </p>
          <ul className="text-sm text-gray-500 space-y-1">
            <li>• One-way trips</li>
            <li>• Round trips</li>
            <li>• Multi-leg journeys</li>
            <li>• Wait time included</li>
          </ul>
        </div>
      </div>

    </div>
  );
};

const Step2ClaimInfo = ({ 
  formData, 
  formOptions, 
  allClaims, 
  handleInputChange, 
  handleClaimantChange, 
  handleClaimChange, 
  clearClaimantSelection, 
  onAddClaimClick,
  onAddClaimantClick 
}) => {
  const availableClaims = (formData.claimantId && (formOptions.claims || []).length > 0 ? formOptions.claims : allClaims || []);
  const hasNoClaims = availableClaims.length === 0;
  const [showClaimNoResults, setShowClaimNoResults] = useState(false);
  const [showClaimantNoResults, setShowClaimantNoResults] = useState(false);
  const [claimSearchTerm, setClaimSearchTerm] = useState('');
  const [claimantSearchTerm, setClaimantSearchTerm] = useState('');

  const handleAddClaimClick = () => {
    onAddClaimClick(claimSearchTerm.trim());
  };

  const handleAddClaimantClick = () => {
    onAddClaimantClick(claimantSearchTerm.trim());
  };
  
  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Claim & Claimant</h2>
        <p className="text-gray-600">Search for a claim to auto-populate claimant information</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <SearchableSelect
            label="Select Claimant"
            value={formData.claimantId}
            onChange={(e) => handleClaimantChange(e.target.value)}
            onClear={() => handleClaimantChange('')}
            options={(formOptions.claimants || []).map(claimant => {
              const name = claimant.first_name && claimant.last_name 
                ? `${claimant.first_name} ${claimant.last_name}`
                : claimant.name || `Claimant ${claimant.id}`;
              const dob = claimant.date_of_birth 
                ? (() => {
                    const datePart = (typeof claimant.date_of_birth === 'string' ? claimant.date_of_birth : '').split('T')[0];
                    if (!datePart || !/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return '';
                    const [y, m, d] = datePart.split('-').map((n) => parseInt(n, 10));
                    return `${String(m).padStart(2, '0')}/${String(d).padStart(2, '0')}/${y}`;
                  })()
                : '';
              const dobText = dob ? ` - DOB: ${dob}` : '';
              return {
                value: claimant.id.toString(),
                label: `${name}${dobText}`
              };
            })}
            placeholder="Select claimant or use claim search"
            required
            onEmptyResultsChange={(noResults) => setShowClaimantNoResults(noResults)}
            onSearchTermChange={setClaimantSearchTerm}
          />
          {showClaimantNoResults && (
            <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start">
                <InformationCircleIcon className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-800 mb-2">
                    No matching claimants found
                  </p>
                  <p className="text-xs text-blue-700 mb-3">
                    Create a claimant to auto-fill this section.
                  </p>
                  <button
                    type="button"
                    onClick={handleAddClaimantClick}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium flex items-center"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add New Claimant
                  </button>
                </div>
              </div>
            </div>
          )}
          {formData.claimantId && (
            <button
              type="button"
              onClick={clearClaimantSelection}
              className="mt-2 text-sm text-red-600 hover:text-red-800 flex items-center"
            >
              <XMarkIcon className="h-4 w-4 mr-1" />
              Clear selection
            </button>
          )}
        </div>
        
        <div>
          <SearchableSelect
            label="Select Claim"
            value={formData.claimId}
            onChange={(e) => handleClaimChange(e.target.value)}
            onClear={() => handleClaimChange('')}
            options={availableClaims.map(claim => {
              const claimantName = claim.claimant_first_name && claim.claimant_last_name 
                ? `${claim.claimant_first_name} ${claim.claimant_last_name}` 
                : claim.claimant_name || '';
              const claimLabel = claimantName 
                ? `${claimantName} - ${claim.claim_number} - ${claim.case_type}`
                : `${claim.claim_number} - ${claim.case_type}`;
              return {
                value: claim.id.toString(),
                label: claimLabel
              };
            })}
            placeholder="Search claim number (auto-populates claimant)"
            required
            onEmptyResultsChange={(noResults) => setShowClaimNoResults(noResults)}
            onSearchTermChange={setClaimSearchTerm}
          />
          {showClaimNoResults && (
            <div className="mt-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start">
                <InformationCircleIcon className="h-5 w-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-800 mb-2">
                    No matching claims found
                  </p>
                  <p className="text-xs text-yellow-700 mb-3">
                    Add a new claim to continue with this request.
                  </p>
                  <button
                    type="button"
                    onClick={handleAddClaimClick}
                    className="px-3 py-1.5 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm font-medium flex items-center"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add New Claim
                  </button>
                </div>
              </div>
            </div>
          )}
          {hasNoClaims && (
            <div className="mt-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start">
                <InformationCircleIcon className="h-5 w-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-800 mb-2">
                    No claims found
                  </p>
                  <p className="text-xs text-yellow-700 mb-3">
                    If the claim you're looking for doesn't exist, you may need to add it first.
                  </p>
                  <button
                    type="button"
                    onClick={handleAddClaimClick}
                    className="px-3 py-1.5 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm font-medium flex items-center"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add New Claim
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {formData.claimantId && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center mb-2">
            <InformationCircleIcon className="h-5 w-5 text-blue-600 mr-2" />
            <span className="text-sm font-medium text-blue-800">
              Claimant Information Auto-filled
            </span>
          </div>
          <p className="text-xs text-blue-600">
            The claimant's information has been automatically populated and will be used for the appointment.
          </p>
        </div>
      )}
      
      {formData.claimId && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center mb-3">
            <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
            <span className="text-sm font-medium text-green-800">
              Claim Details
            </span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Date of Birth</p>
              <p className="text-sm text-gray-900">
                {formData.claimantDateOfBirth 
                  ? (() => {
                      const dateStr = formData.claimantDateOfBirth;
                      let date;
                      if (dateStr.includes('-') && !dateStr.includes('T')) {
                        const [year, month, day] = dateStr.split('-').map(Number);
                        date = new Date(year, month - 1, day);
                      } else {
                        date = new Date(dateStr);
                      }
                      if (isNaN(date.getTime())) return 'Not available';
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      const year = date.getFullYear();
                      return `${month}/${day}/${year}`;
                    })()
                  : 'Not available'}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Date of Injury</p>
              <p className="text-sm text-gray-900">
                {formData.dateOfInjury 
                  ? (() => {
                      const dateStr = formData.dateOfInjury;
                      if (dateStr.includes('-') && !dateStr.includes('T')) {
                        const [year, month, day] = dateStr.split('-').map(Number);
                        return new Date(year, month - 1, day).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        });
                      }
                      return new Date(dateStr).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      });
                    })()
                  : 'Not available'}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Employer</p>
              <p className="text-sm text-gray-900">
                {formData.employer || 'Not available'}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Phone Number</p>
              <p className="text-sm text-gray-900">
                {formData.claimantPhone || 'Not available'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Step3AppointmentDetails = ({
  formData,
  handleInputChange,
  handleReserveHoursChange,
  calculateEndTime,
  defaultReserveHours = 2,
}) => {
  const addRepeatedAppointment = () => {
    // Get time values from the last appointment if it exists, otherwise use formData defaults
    const lastAppointment = formData.repeatedAppointments.length > 0 
      ? formData.repeatedAppointments[formData.repeatedAppointments.length - 1]
      : null;
    
    const newAppointment = {
      date: '',
      time: lastAppointment?.time || formData.startTime || '',
      arrivalTime: lastAppointment?.arrivalTime || formData.arrivalTime || formData.startTime || ''
    };
    handleInputChange('repeatedAppointments', [...formData.repeatedAppointments, newAppointment]);
  };

  const removeRepeatedAppointment = (index) => {
    const updated = formData.repeatedAppointments.filter((_, i) => i !== index);
    handleInputChange('repeatedAppointments', updated);
  };

  const updateRepeatedAppointment = (index, field, value) => {
    const updated = [...formData.repeatedAppointments];
    updated[index] = { ...updated[index], [field]: value };
    handleInputChange('repeatedAppointments', updated);
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Appointment Details</h2>
        <p className="text-gray-600">Set the date, time, and type of appointment</p>
      </div>

      {/* Repeated Appointment Toggle */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={formData.isRepeatedAppointment}
            onChange={(e) => {
              handleInputChange('isRepeatedAppointment', e.target.checked);
              if (!e.target.checked) {
                handleInputChange('repeatedAppointments', []);
              }
            }}
            className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <span className="ml-3 text-sm font-medium text-gray-900">
            This is a repeated appointment
          </span>
        </label>
        <p className="mt-2 ml-8 text-xs text-gray-600">
          Check this if you need to create multiple appointments with the same details but different dates/times
        </p>
      </div>

      {!formData.isRepeatedAppointment ? (
        // Single Appointment Form
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Date"
            type="date"
            value={formData.appointmentDate}
            onChange={(e) => handleInputChange('appointmentDate', e.target.value)}
            onClear={() => handleInputChange('appointmentDate', '')}
            min={new Date().toISOString().split('T')[0]}
            required
          />
          
          <Input
            label="Start Time"
            type="time"
            value={formData.startTime}
            onChange={(e) => handleInputChange('startTime', e.target.value)}
            onClear={() => handleInputChange('startTime', '')}
            required
          />
          
          {formData.serviceType === 'document' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estimated Word Count
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="1"
                  value={formData.estimatedWordCount}
                  onChange={(e) => handleInputChange('estimatedWordCount', e.target.value)}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${formData.estimatedWordCount ? 'pr-10' : ''}`}
                  placeholder="Enter estimated word count"
                />
                {formData.estimatedWordCount && (
                  <button
                    type="button"
                    onClick={() => handleInputChange('estimatedWordCount', '')}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Enter the estimated number of words in the document to be translated
              </p>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reserve Hours <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.5"
                  min={defaultReserveHours}
                  max="12"
                  value={formData.reserveHours}
                  onChange={(e) => handleReserveHoursChange(e.target.value)}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${formData.reserveHours ? 'pr-10' : ''}`}
                  placeholder={`Default: ${defaultReserveHours} hours`}
                />
                {formData.reserveHours && (
                  <button
                    type="button"
                    onClick={() => handleInputChange('reserveHours', '')}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Leave blank to use the default. Minimum 2 hours for certified appointments and 3 hours for legal appointments.
              </p>
              {formData.startTime && formData.reserveHours && (
                <p className="mt-1 text-sm text-blue-600">
                  Calculated End Time: {calculateEndTime(formData.startTime, formData.reserveHours)}
                </p>
              )}
            </div>
          )}
          
          <Input
            label="Arrival Time"
            type="time"
            value={formData.arrivalTime}
            onChange={(e) => handleInputChange('arrivalTime', e.target.value)}
            onClear={() => handleInputChange('arrivalTime', '')}
            placeholder="Defaults to start time if blank"
          />
          
          <div className="md:col-span-2">
            <SearchableSelect
              label="Appointment Type"
              value={formData.appointmentType}
              onChange={(e) => handleInputChange('appointmentType', e.target.value)}
              onClear={() => handleInputChange('appointmentType', '')}
              options={appointmentTypeOptions}
              placeholder="Select appointment type"
              required
            />
          </div>
          
          {formData.appointmentType === 'other' && (
            <div className="md:col-span-2">
              <Input
                label="Custom Appointment Type"
                value={formData.customAppointmentType}
                onChange={(e) => handleInputChange('customAppointmentType', e.target.value)}
                onClear={() => handleInputChange('customAppointmentType', '')}
                placeholder="Please specify the appointment type"
                required
              />
            </div>
          )}
        </div>
      ) : (
        // Repeated Appointments Form
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Add Multiple Appointment Dates & Times</h3>
            <div className="space-y-3">
              {formData.repeatedAppointments.map((appointment, index) => (
                <div key={index} className="bg-white border border-gray-300 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-700">Appointment {index + 1}</span>
                    <button
                      type="button"
                      onClick={() => removeRepeatedAppointment(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      label="Date"
                      type="date"
                      value={appointment.date}
                      onChange={(e) => updateRepeatedAppointment(index, 'date', e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                    <Input
                      label="Start Time"
                      type="time"
                      value={appointment.time}
                      onChange={(e) => updateRepeatedAppointment(index, 'time', e.target.value)}
                      required
                    />
                    <Input
                      label="Arrival Time"
                      type="time"
                      value={appointment.arrivalTime}
                      onChange={(e) => updateRepeatedAppointment(index, 'arrivalTime', e.target.value)}
                      placeholder="Defaults to start time if blank"
                    />
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={addRepeatedAppointment}
                className="w-full flex items-center justify-center px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Add Another Appointment
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {formData.serviceType === 'document' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estimated Word Count
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    value={formData.estimatedWordCount}
                    onChange={(e) => handleInputChange('estimatedWordCount', e.target.value)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${formData.estimatedWordCount ? 'pr-10' : ''}`}
                    placeholder="Enter estimated word count"
                  />
                  {formData.estimatedWordCount && (
                    <button
                      type="button"
                      onClick={() => handleInputChange('estimatedWordCount', '')}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Enter the estimated number of words in the document to be translated
                </p>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reserve Hours <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.5"
                    min={defaultReserveHours}
                    max="12"
                    value={formData.reserveHours}
                    onChange={(e) => handleReserveHoursChange(e.target.value)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${formData.reserveHours ? 'pr-10' : ''}`}
                    placeholder={`Default: ${defaultReserveHours} hours`}
                  />
                  {formData.reserveHours && (
                    <button
                      type="button"
                      onClick={() => handleInputChange('reserveHours', '')}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  This will apply to all appointments
                </p>
              </div>
            )}
            
            <div className="md:col-span-2">
              <SearchableSelect
                label="Appointment Type"
                value={formData.appointmentType}
                onChange={(e) => handleInputChange('appointmentType', e.target.value)}
                onClear={() => handleInputChange('appointmentType', '')}
                options={appointmentTypeOptions}
                placeholder="Select appointment type"
                required
              />
            </div>
            
            {formData.appointmentType === 'other' && (
              <div className="md:col-span-2">
                <Input
                  label="Custom Appointment Type"
                  value={formData.customAppointmentType}
                  onChange={(e) => handleInputChange('customAppointmentType', e.target.value)}
                  onClear={() => handleInputChange('customAppointmentType', '')}
                  placeholder="Please specify the appointment type"
                  required
                />
              </div>
            )}
          </div>
        </div>
      )}

      <div>
        <Input
          label="Appointment Notes"
          value={formData.appointmentNotes || ''}
          onChange={(e) => handleInputChange('appointmentNotes', e.target.value)}
          onClear={() => handleInputChange('appointmentNotes', '')}
          placeholder="Additional details about the appointment..."
        />
      </div>
    </div>
  );
};

const Step4InterpreterRequirements = ({ formData, formOptions, handleInputChange }) => {
  // Determine if interpreter type should be shown
  const appointmentCategory = formData.appointmentType ? getAppointmentCategory(formData.appointmentType) : null;
  // Show interpreter type for all service types (onsite, video, phone)
  const showInterpreterType = formData.serviceType && formData.serviceType !== '';
  // Interpreter type is required for:
  // - Video and phone service types (always required)
  // - Onsite medical-standard appointments
  const isVideoOrPhone = formData.serviceType === 'video' || formData.serviceType === 'video_medical' || formData.serviceType === 'phone' || formData.serviceType === 'phone_conference';
  const isOnsiteMedicalStandard = formData.serviceType === 'onsite' && appointmentCategory === 'medical-standard';
  const isInterpreterTypeRequired = isVideoOrPhone || isOnsiteMedicalStandard;
  
  // Check if Video service type is selected (Legal or Medical)
  const isVideoRemote = formData.serviceType === 'video' || formData.serviceType === 'video_medical';
  
  // Check if interpreter type was auto-set (for Legal or Medical Certified)
  const isInterpreterTypeAutoSet = formData.serviceType === 'onsite' && 
    (appointmentCategory === 'legal' || appointmentCategory === 'medical-legal');

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Interpreter Requirements</h2>
        <p className="text-gray-600">Specify the language and interpreter type needed</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <SearchableSelect
            label="Language"
            value={formData.language}
            onChange={(e) => {
              handleInputChange('language', e.target.value);
              // Clear custom language if not "other"
              if (e.target.value !== 'other') {
                handleInputChange('customLanguage', '');
              }
            }}
            onClear={() => {
              handleInputChange('language', '');
              handleInputChange('customLanguage', '');
            }}
            options={[
              ...(formOptions.languages || []).filter(lang => lang.name.toLowerCase() !== 'agency').map(lang => ({ value: lang.id, label: lang.name })),
              { value: 'other', label: 'Other' }
            ]}
            placeholder="Select language"
            required
          />
          {formData.language === 'other' && (
            <div className="mt-2">
              <Input
                label="Enter Language"
                value={formData.customLanguage}
                onChange={(e) => handleInputChange('customLanguage', e.target.value)}
                placeholder="Please specify the language"
                required
              />
            </div>
          )}
        </div>
        
        <SearchableSelect
          label="Service Type"
          value={formData.serviceType}
          onChange={(e) => handleInputChange('serviceType', e.target.value)}
          onClear={() => handleInputChange('serviceType', '')}
          options={serviceTypeOptions}
          placeholder="Select service type"
          required
        />
        
        {showInterpreterType && (
          <SearchableSelect
            label="Interpreter Type"
            value={formData.interpreterType}
            onChange={(e) => handleInputChange('interpreterType', e.target.value)}
            onClear={() => handleInputChange('interpreterType', '')}
            options={(formOptions.interpreterTypes || []).map(type => ({
              value: type.id.toString(),
              label: type.name
            }))}
            placeholder="Select interpreter type"
            required={isInterpreterTypeRequired}
          />
        )}
        
        {isInterpreterTypeAutoSet && formData.interpreterType && (
          <div className="md:col-span-2">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                ℹ️ Interpreter type automatically set based on appointment type: <strong>
                  {formOptions.interpreterTypes?.find(it => it.id.toString() === formData.interpreterType)?.name}
                </strong>
              </p>
            </div>
          </div>
        )}
      </div>

      {isVideoRemote && (
        <div>
          <Input
            label="Meeting Link (Optional)"
            value={formData.meetingLink || ''}
            onChange={(e) => handleInputChange('meetingLink', e.target.value)}
            onClear={() => handleInputChange('meetingLink', '')}
            placeholder="e.g., https://zoom.us/j/123456789 or https://meet.google.com/abc-defg-hij"
            type="url"
          />
          <p className="mt-1 text-xs text-gray-500">
            💡 If you have a specific meeting link (Zoom, Google Meet, etc.), you can provide it here.
          </p>
        </div>
      )}

      <div>
        <Input
          label="Special Requirements"
          value={formData.specialRequirements}
          onChange={(e) => handleInputChange('specialRequirements', e.target.value)}
          onClear={() => handleInputChange('specialRequirements', '')}
          placeholder="Any special requirements or notes"
        />
      </div>
    </div>
  );
};

const Step5Location = ({ 
  formData, 
  formOptions, 
  handleInputChange, 
  handleServiceLocationSelect, 
  locationAutocompleteRef, 
  mapsInitialized,
  locationContacts,
  onLocationContactSelect,
  showNewContactModal,
  setShowNewContactModal,
  newContactFormData,
  setNewContactFormData,
  handleCreateLocationContact,
  makeAuthenticatedRequest
}) => {
  const isLocationOptional = interpretingJobServiceLocationOptional(formData);

  return (
  <div className="space-y-6">
    <div className="text-center mb-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Location Details</h2>
      <p className="text-gray-600">
        {isLocationOptional
          ? 'Optionally provide the location of service and contact information'
          : 'Specify the appointment location'}
      </p>
    </div>

    <div className="space-y-6">
      {/* Service Location Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Service Location{' '}
          {!isLocationOptional && <span className="text-red-500">*</span>}
        </label>
        <ServiceLocationSelector
          selectedLocation={formData.selectedServiceLocation}
          onLocationSelect={handleServiceLocationSelect}
          placeholder="Select a service location or add a new one"
          showAddNew={true}
        />
        <p className="mt-1 text-xs text-gray-500">
          💡 Select from existing locations or add a new one. This will auto-fill the address and phone number.
          {isLocationOptional && (
            <span className="block mt-1 text-gray-600">
              Location is optional for video remote interpretation appointments.
            </span>
          )}
        </p>
      </div>

      {/* Location Contact Selection - Show only if service location is selected */}
      {formData.selectedServiceLocation && formData.serviceLocationId && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <label className="block text-sm font-medium text-gray-700">
              Doctor Name{' '}
              {!formData.locationContactId && <span className="text-red-500">*</span>}
            </label>
            <button
              type="button"
              onClick={() => {
                setShowNewContactModal(true);
              }}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center font-medium"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add New Contact
            </button>
          </div>
          
          {locationContacts && locationContacts.length > 0 ? (
            <>
              <SearchableSelect
                value={formData.locationContactId}
                onChange={(e) => onLocationContactSelect(e.target.value)}
                options={locationContacts.map(contact => ({
                  value: contact.id.toString(),
                  label: `${contact.name}${contact.title ? ` - ${contact.title}` : ''}`
                }))}
                placeholder="Select a location contact"
                required
              />
              <p className="mt-2 text-xs text-gray-600">
                💡 Please select a contact person for this location or add a new one using the button above.
              </p>
            </>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-gray-700 mb-3">
                No location contacts found for this service location.
              </p>
              <button
                type="button"
                onClick={() => {
                  setShowNewContactModal(true);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center mx-auto"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add First Location Contact
              </button>
              <p className="mt-2 text-xs text-gray-600">
                💡 {isLocationOptional
                  ? 'Add a location contact if you would like to include contact details for this location.'
                  : 'You must add at least one location contact to continue.'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>

  </div>
  );
};

/** Read-only summary for the final step (PDF attachment) */
const AppointmentRequestSummaryCard = ({
  formData,
  formOptions,
  allClaims,
  formatDate,
  calculateEndTime
}) => {
  const claimantName = [formData.claimantFirstName, formData.claimantLastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  const claimIdNum = formData.claimId ? parseInt(formData.claimId, 10) : null;
  const claim =
    (formOptions.claims || []).find((c) => c.id === claimIdNum) ||
    (allClaims || []).find((c) => c.id === claimIdNum);

  const apptTypeLabel =
    formData.appointmentType === 'other'
      ? formData.customAppointmentType || 'Other'
      : appointmentTypeOptions.find((o) => o.value === formData.appointmentType)?.label ||
        formData.appointmentType ||
        '—';

  const serviceLabel =
    serviceTypeOptions.find((o) => o.value === formData.serviceType)?.label ||
    formData.serviceType ||
    '—';

  const languageLabel =
    formData.language === 'other'
      ? formData.customLanguage || 'Other'
      : (formOptions.languages || []).find(
          (l) => l.id?.toString() === formData.language?.toString()
        )?.name || formData.language || '—';

  const interpreterLabel = (formOptions.interpreterTypes || []).find(
    (it) => it.id?.toString() === formData.interpreterType?.toString()
  )?.name;

  const locationContact = (formData.locationContacts || []).find(
    (lc) => lc.id?.toString() === formData.locationContactId?.toString()
  );

  const reserveServiceTypeKey = resolveReserveServiceTypeKey(formData, formOptions);
  const defaultReserveHours = getDefaultReserveHours(reserveServiceTypeKey);
  const reserveDisplay =
    formData.serviceType === 'document'
      ? null
      : formData.reserveHours !== '' && formData.reserveHours != null
        ? `${formData.reserveHours} hr`
        : `${defaultReserveHours} hr (default)`;

  /** HTML time value (HH:MM or HH:MM:SS) → e.g. "3:28 PM" */
  const formatTime12h = (t) => {
    if (!t || typeof t !== 'string') return '—';
    const part = t.trim().length >= 5 ? t.trim().slice(0, 5) : t.trim();
    const [hStr, mStr] = part.split(':');
    const h = parseInt(hStr, 10);
    const m = parseInt(mStr, 10);
    if (Number.isNaN(h) || Number.isNaN(m)) return '—';
    const period = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    const mm = String(m).padStart(2, '0');
    return `${hour12}:${mm} ${period}`;
  };

  const truncate = (text, max = 220) => {
    if (!text || typeof text !== 'string') return null;
    const s = text.trim();
    if (!s) return null;
    return s.length > max ? `${s.slice(0, max)}…` : s;
  };

  const rows = [];

  rows.push({ label: 'Claimant', value: claimantName || '—' });
  rows.push({
    label: 'Claim',
    value: claim
      ? [claim.claim_number, claim.case_type].filter(Boolean).join(' · ') || '—'
      : '—'
  });
  rows.push({ label: 'Service', value: serviceLabel });
  rows.push({ label: 'Appointment type', value: apptTypeLabel });
  rows.push({
    label: 'Format',
    value: formData.isRemote ? 'Remote' : 'In person'
  });

  if (formData.isRepeatedAppointment && formData.repeatedAppointments?.length) {
    rows.push({
      label: 'Schedule',
      value: (
        <ul className="list-disc pl-4 space-y-1 mt-0.5">
          {formData.repeatedAppointments.map((appt, i) => (
            <li key={i}>
              {appt.date ? formatDate(appt.date) : '—'} · Start {formatTime12h(appt.time)}
              {appt.arrivalTime && appt.arrivalTime !== appt.time
                ? ` · Arrival ${formatTime12h(appt.arrivalTime)}`
                : ''}
            </li>
          ))}
        </ul>
      )
    });
    if (formData.serviceType === 'document' && formData.estimatedWordCount) {
      rows.push({
        label: 'Est. word count',
        value: String(formData.estimatedWordCount)
      });
    } else if (reserveDisplay) {
      rows.push({ label: 'Reserve (each)', value: reserveDisplay });
    }
  } else {
    rows.push({
      label: 'Date',
      value: formData.appointmentDate ? formatDate(formData.appointmentDate) : '—'
    });
    rows.push({
      label: 'Start / arrival',
      value: `${formatTime12h(formData.startTime)} / ${formatTime12h(formData.arrivalTime || formData.startTime)}`
    });
    if (formData.serviceType === 'document' && formData.estimatedWordCount) {
      rows.push({
        label: 'Est. word count',
        value: String(formData.estimatedWordCount)
      });
    } else if (reserveDisplay) {
      rows.push({ label: 'Reserve hours', value: reserveDisplay });
      if (formData.startTime && formData.serviceType !== 'document') {
        const effReserve =
          formData.reserveHours !== '' && formData.reserveHours != null
            ? Number(formData.reserveHours)
            : defaultReserveHours;
        const end = calculateEndTime(formData.startTime, effReserve);
        if (end) rows.push({ label: 'End time', value: formatTime12h(end) });
      }
    }
  }

  rows.push({ label: 'Language', value: languageLabel });
  if (interpreterLabel) {
    rows.push({ label: 'Interpreter type', value: interpreterLabel });
  }

  if (formData.meetingLink?.trim()) {
    rows.push({ label: 'Meeting link', value: truncate(formData.meetingLink, 120) });
  }

  if (formData.selectedServiceLocation) {
    const loc = formData.selectedServiceLocation;
    const addr = [loc.name, loc.address].filter(Boolean).join(' — ') || loc.address || '—';
    rows.push({ label: 'Service location', value: addr });
    if (locationContact?.name) {
      rows.push({ label: 'Location contact', value: locationContact.name });
    }
  }

  if (formData.phone?.trim()) {
    rows.push({ label: 'Appointment phone', value: formData.phone.trim() });
  }

  const notes = truncate(formData.appointmentNotes);
  if (notes) rows.push({ label: 'Notes', value: notes });

  const spec = truncate(formData.specialRequirements);
  if (spec) rows.push({ label: 'Special requirements', value: spec });

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 text-left shadow-sm">
      <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
        <DocumentTextIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
        Appointment request summary
      </h3>
      <p className="text-xs text-slate-600 mb-4">
        Review your details before submitting. You can use Back to change anything.
      </p>
      <dl className="space-y-3 text-sm">
        {rows.map((row, idx) => (
          <div key={idx} className="grid grid-cols-1 sm:grid-cols-[8.5rem_1fr] gap-1 sm:gap-3">
            <dt className="font-medium text-slate-600">{row.label}</dt>
            <dd className="text-slate-900 break-words">{row.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

const Step6Attachment = ({
  formData,
  formOptions,
  allClaims,
  handleInputChange,
  calculateEndTime,
  formatDate
}) => {
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (file.type !== 'application/pdf') {
        toast.error('Please select a PDF file');
        return;
      }
      
      // Validate file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        toast.error('File size must be less than 10MB');
        return;
      }
      
      handleInputChange('attachment', file);
    }
  };

  const removeAttachment = () => {
    handleInputChange('attachment', null);
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Optional PDF Attachment</h2>
        <p className="text-gray-600">Attach any relevant documents (optional)</p>
      </div>

      <AppointmentRequestSummaryCard
        formData={formData}
        formOptions={formOptions}
        allClaims={allClaims}
        formatDate={formatDate}
        calculateEndTime={calculateEndTime}
      />

      <div className="space-y-6">
        {!formData.attachment ? (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
            <div className="flex flex-col items-center">
              <svg className="h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <label htmlFor="attachment" className="cursor-pointer">
                <span className="text-lg font-medium text-gray-700">Upload PDF Document</span>
                <p className="text-sm text-gray-500 mt-1">Click to select a PDF file (max 10MB)</p>
              </label>
              <input
                id="attachment"
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg className="h-8 w-8 text-red-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-gray-900">{formData.attachment.name}</p>
                  <p className="text-xs text-gray-500">
                    {(formData.attachment.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={removeAttachment}
                className="text-red-500 hover:text-red-700 p-1"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}
        
        <p className="text-xs text-gray-500 text-center">
          💡 You can attach relevant documents like medical records, legal documents, or other supporting materials.
        </p>
      </div>
    </div>
  );
};

// Helper function to calculate end time from start time and reserve hours
const calculateEndTime = (startTime, reserveHours) => {
  if (!startTime || !reserveHours) return '';
  const start = new Date(`2000-01-01T${startTime}`);
  const end = new Date(start.getTime() + (reserveHours * 60 * 60 * 1000));
  return end.toTimeString().slice(0, 5); // Return HH:MM format
};

const NewAppointment = () => {
  const { makeAuthenticatedRequest, customer } = useAuth();
  const {
    viewClaim,
    pendingClaim,
    saveAssignment,
    skipPrompt,
    saving: savingRole
  } = useViewExistingClaim(makeAuthenticatedRequest, customer);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [isReRequest, setIsReRequest] = useState(false);
  const [initialUrlParams, setInitialUrlParams] = useState({ claimantId: null, claimId: null });
  const [originalAppointment, setOriginalAppointment] = useState(null);
  const [showSearchAnimation, setShowSearchAnimation] = useState(false);
  const [appointmentId, setAppointmentId] = useState(null);
  const [searchInterval, setSearchInterval] = useState(null);
  const [searchStartTime, setSearchStartTime] = useState(null);
  const [mapsInitialized, setMapsInitialized] = useState(false);
  const locationAutocompleteRef = useRef(null);
  const claimantAddressAutocompleteRef = useRef(null);
  const [jobType, setJobType] = useState('interpreting'); // 'interpreting' or 'transportation'
  const [showServiceTypeSelection, setShowServiceTypeSelection] = useState(false);
  const [formOptions, setFormOptions] = useState({
    claimants: [],
    claims: [],
    interpreterTypes: [],
    serviceTypes: [],
    languages: []
  });
  const [allClaims, setAllClaims] = useState([]);
  const [formData, setFormData] = useState({
    // Appointment Details
    appointmentDate: '',
    startTime: '',
    reserveHours: '',
    estimatedWordCount: '',
    arrivalTime: '',
    appointmentType: '',
    customAppointmentType: '',
    appointmentNotes: '',
    isRemote: false,
    locationAddress: '',
    locationCity: '',
    locationState: '',
    locationZipCode: '',
    phone: '',
    serviceLocationId: '',
    selectedServiceLocation: null,
    locationContactId: '',
    locationContacts: [],
    // Repeated appointments
    isRepeatedAppointment: false,
    repeatedAppointments: [], // Array of { date: '', time: '', arrivalTime: '' }
    
    // Claimant & Claim Selection
    claimantId: '',
    claimId: '',
    
    // Auto-populated Claimant Information (read-only)
    claimantFirstName: '',
    claimantLastName: '',
    claimantGender: '',
    claimantDateOfBirth: '',
    claimantPhone: '',
    claimantLanguage: '',
    claimantAddress: '',
    claimantAddressLatitude: '',
    claimantAddressLongitude: '',
    claimantEmployerInsured: '',
    
    // Claim Information (populated when claim is selected)
    dateOfInjury: '',
    employer: '',
    
    // Interpreter Requirements
    language: '',
    customLanguage: '',
    interpreterType: '',
    serviceType: '',
    specialRequirements: '',
    meetingLink: '',
    
    // PDF Attachment
    attachment: null
  });
  const [showAddClaimModal, setShowAddClaimModal] = useState(false);
  const [isSavingClaim, setIsSavingClaim] = useState(false);
  const [newClaimData, setNewClaimData] = useState(initialNewClaimState);
  const [newClaimNumberWarning, setNewClaimNumberWarning] = useState(null);
  const [newClaimantNameWarning, setNewClaimantNameWarning] = useState(null);
  const [duplicateAppointmentWarning, setDuplicateAppointmentWarning] = useState(null);
  const [duplicateModal, setDuplicateModal] = useState(null);
  const lastShownAppointmentDupRef = useRef(null);
  const newClaimantNamesRef = useRef({ firstName: '', lastName: '' });
  const [showAddClaimantModal, setShowAddClaimantModal] = useState(false);
  const [isSavingClaimant, setIsSavingClaimant] = useState(false);
  const [newClaimantData, setNewClaimantData] = useState(initialNewClaimantState);
  const [customers, setCustomers] = useState([]);

  // Multi-step form state
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const totalSteps = 6;
  const isStep1PrimaryAction = currentStep === 1 && !showServiceTypeSelection;
  const step1PrimaryButtonLabel = jobType
    ? `Continue with ${jobType === 'interpreting' ? 'Interpreting' : 'Transportation'} Service`
    : 'Select a service to continue';

  // Cleanup interval on component unmount
  useEffect(() => {
    return () => {
      if (searchInterval) {
        clearInterval(searchInterval);
      }
    };
  }, [searchInterval]);

  // Reset service type selection overlay when leaving step 1
  useEffect(() => {
    if (currentStep !== 1 && showServiceTypeSelection) {
      setShowServiceTypeSelection(false);
    }
  }, [currentStep, showServiceTypeSelection]);

  // Update search duration for animation
  useEffect(() => {
    let durationInterval;
    if (showSearchAnimation && searchStartTime) {
      durationInterval = setInterval(() => {
        // Force re-render to update search duration
        setSearchStartTime(prev => prev);
      }, 1000);
    }
    return () => {
      if (durationInterval) {
        clearInterval(durationInterval);
      }
    };
  }, [showSearchAnimation, searchStartTime]);

  const loadOriginalAppointment = useCallback(async (appointmentId) => {
    try {
      const data = await makeAuthenticatedRequest(`/appointments/${appointmentId}`);
      
      if (data.success) {
        setOriginalAppointment(data.data);
        return data.data;
      } else {
        toast.error(data.message || 'Failed to load original appointment');
        return null;
      }
    } catch (error) {
      console.error('Error loading original appointment:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to load original appointment'));
      return null;
    }
  }, [makeAuthenticatedRequest]);

  const loadFormOptions = useCallback(async () => {
    try {
      const billingAccountId = searchParams.get('billingAccountId');
      const claimantsEndpoint = billingAccountId
        ? `/claimants?scope=all&billingAccountId=${encodeURIComponent(billingAccountId)}`
        : '/claimants';
      const [claimantsData, interpreterTypesData, serviceTypesData, languagesData, allClaimsData, customersData] = await Promise.all([
        makeAuthenticatedRequest(claimantsEndpoint),
        makeAuthenticatedRequest('/interpreter-types'),
        makeAuthenticatedRequest('/parametric/service-types'),
        makeAuthenticatedRequest('/languages'),
        makeAuthenticatedRequest('/claims'),
        makeAuthenticatedRequest('/customers')
      ]);
      
      setFormOptions({
        claimants: claimantsData.success ? claimantsData.data : [],
        interpreterTypes: interpreterTypesData.success ? interpreterTypesData.data : [],
        serviceTypes: serviceTypesData.success ? serviceTypesData.data : [],
        languages: languagesData.success ? languagesData.data : []
      });
      
      // Store all claims for searchable dropdown
      setAllClaims(allClaimsData.success ? allClaimsData.data : []);
      setCustomers(customersData.success ? customersData.data : []);
    } catch (error) {
      console.error('Error loading form options:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to load form options'));
    }
  }, [makeAuthenticatedRequest, searchParams]);

  const loadClaimsForClaimant = useCallback(async (claimantId) => {
    try {
      const data = await makeAuthenticatedRequest(`/claimants/${claimantId}/claims`);
      
      if (data.success) {
        setFormOptions(prev => ({
          ...prev,
          claims: data.data
        }));
        return data.data;
      } else {
        console.error('Failed to load claims:', data.message);
        setFormOptions(prev => ({ ...prev, claims: [] }));
        return [];
      }
    } catch (error) {
      console.error('Error loading claims:', error);
      setFormOptions(prev => ({ ...prev, claims: [] }));
      return [];
    }
  }, [makeAuthenticatedRequest]);

  const populateFormFromOriginalAppointment = useCallback(async (originalAppointment) => {
    // Set job type from original appointment
    setJobType(originalAppointment.job_type || 'interpreting');
    
    // First set the claimant ID to trigger claims loading
    setFormData(prev => ({
      ...prev,
      // Don't copy date/time fields for re-request
      appointmentType: originalAppointment.appointment_type || '',
      appointmentNotes: originalAppointment.appointment_notes || '',
      isRemote: originalAppointment.is_remote || false,
      locationAddress: originalAppointment.location_address || '',
      locationCity: originalAppointment.location_city || '',
      locationState: originalAppointment.location_state || '',
      locationZipCode: originalAppointment.location_zip_code || '',
      phone: originalAppointment.phone || '',
      serviceLocationId: originalAppointment.service_location_id || '',
      selectedServiceLocation: null, // Will be loaded below if serviceLocationId exists
      claimantId: originalAppointment.claimant_id ? originalAppointment.claimant_id.toString() : '',
      language: originalAppointment.source_language_id ? originalAppointment.source_language_id.toString() : '',
      interpreterType: originalAppointment.interpreter_type_id ? originalAppointment.interpreter_type_id.toString() : '',
      // Calculate reserve hours from duration
      reserveHours: originalAppointment.estimated_duration_minutes ? (originalAppointment.estimated_duration_minutes / 60) : '',
      estimatedWordCount: originalAppointment.estimated_word_count ? originalAppointment.estimated_word_count.toString() : '',
      serviceType: originalAppointment.service_type_id ? originalAppointment.service_type_id.toString() : '',
      specialRequirements: originalAppointment.special_requirements || ''
    }));

    // Load claims for the claimant first, then populate claim details
    if (originalAppointment.claimant_id) {
      const loadedClaims = await loadClaimsForClaimant(originalAppointment.claimant_id.toString());
      
      // After claims are loaded, populate the claim details if a claim ID exists
      if (originalAppointment.claim_id && loadedClaims && loadedClaims.length > 0) {
        // Use selectClaimById to properly populate all claim-related fields
        selectClaimById(originalAppointment.claim_id.toString(), loadedClaims);
      }
    }

    // Load service location details if serviceLocationId exists
    if (originalAppointment.service_location_id) {
      try {
        const locationResponse = await makeAuthenticatedRequest(`/service-locations/${originalAppointment.service_location_id}`);
        if (locationResponse.success && locationResponse.data) {
          const location = locationResponse.data;
          // Set the selected service location object
          setFormData(prev => ({
            ...prev,
            selectedServiceLocation: location,
            serviceLocationId: location.id,
            locationAddress: location.address || prev.locationAddress,
            locationCity: location.city || prev.locationCity,
            locationState: location.state || prev.locationState,
            locationZipCode: location.zip_code || prev.locationZipCode,
            phone: location.phone_number || prev.phone
          }));
          
          // Load location contacts for this service location
          await loadLocationContacts(location.id);
          
          // Set location contact if it exists in the original appointment
          if (originalAppointment.location_contact_id) {
            setFormData(prev => ({
              ...prev,
              locationContactId: originalAppointment.location_contact_id.toString()
            }));
          }
        }
      } catch (error) {
        console.error('Error loading service location:', error);
      }
    }
  }, [loadClaimsForClaimant, makeAuthenticatedRequest]); // selectClaimById is a stable function

  // Load form options on component mount
  useEffect(() => {
    loadFormOptions();
  }, [loadFormOptions]);

  // Handle re-request functionality
  useEffect(() => {
    const reRequestId = searchParams.get('reRequest');
    const claimantId = searchParams.get('claimantId');
    const claimId = searchParams.get('claimId');
    
    if (reRequestId) {
      setIsReRequest(true);
      loadOriginalAppointment(reRequestId).then(async (appointment) => {
        if (appointment) {
          // Check if it's a transportation job
          if (appointment.job_type === 'transportation') {
            // Navigate to transportation form with re-request parameter
            navigate(`/transportation/new?reRequest=${reRequestId}`);
          } else {
            // For interpreting jobs, populate form and skip Step 1
            await populateFormFromOriginalAppointment(appointment);
            setCurrentStep(2);
            setCompletedSteps(new Set([1]));
          }
        }
      });
    }
  }, [searchParams, loadOriginalAppointment, populateFormFromOriginalAppointment, navigate]);

  // Capture initial URL parameters once
  useEffect(() => {
    const claimantId = searchParams.get('claimantId');
    const claimId = searchParams.get('claimId');
    
    if (claimantId || claimId) {
      setInitialUrlParams({ claimantId, claimId });
    }
  }, []); // Only run once on mount

  // Note: We no longer skip Step 1 when coming from a claim - users should always choose job type

  // Handle claimant and claim selection from URL parameters
  useEffect(() => {
    if ((formOptions.claimants || []).length > 0 && initialUrlParams.claimantId && !isReRequest) {
      handleClaimantChange(initialUrlParams.claimantId).then(() => {
        if (initialUrlParams.claimId) {
          // Ensure we use the freshly loaded claims list to prevent race conditions
          loadClaimsForClaimant(initialUrlParams.claimantId).then((claims) => {
            selectClaimById(initialUrlParams.claimId, claims || []);
          });
        }
      });
    }
  }, [formOptions.claimants, initialUrlParams, isReRequest]);

  // Load claims when claimant is selected
  useEffect(() => {
    if (formData.claimantId) {
      loadClaimsForClaimant(formData.claimantId);
    } else {
      setFormOptions(prev => ({ ...prev, claims: [] }));
      setFormData(prev => ({ ...prev, claimId: '' }));
    }
  }, [formData.claimantId, loadClaimsForClaimant]);

  const buildDuplicateCheckParams = useCallback((overrides = {}) => {
    const appointmentDate = overrides.appointmentDate ?? formData.appointmentDate;
    const startTime = overrides.startTime ?? formData.startTime;
    const isRemote = overrides.isRemote ?? formData.isRemote;
    const params = new URLSearchParams({
      claimantId: String(overrides.claimantId ?? formData.claimantId),
      appointmentDate: String(appointmentDate),
      startTime: String(startTime),
      isRemote: String(isRemote)
    });
    if (formData.serviceLocationId) {
      params.set('serviceLocationId', String(formData.serviceLocationId));
      const loc = formData.selectedServiceLocation;
      if (loc?.address) params.set('locationAddress', loc.address);
      if (loc?.city) params.set('locationCity', loc.city);
      if (loc?.state) params.set('locationState', loc.state);
      if (loc?.zip_code) params.set('locationZipCode', loc.zip_code);
      else if (formData.locationZipCode) params.set('locationZipCode', formData.locationZipCode);
    }
    return params;
  }, [formData]);

  const checkDuplicateAppointment = useCallback(async (overrides = {}) => {
    if (isReRequest) {
      setDuplicateAppointmentWarning(null);
      return null;
    }

    const claimantId = overrides.claimantId ?? formData.claimantId;
    const appointmentDate = overrides.appointmentDate ?? formData.appointmentDate;
    const startTime = overrides.startTime ?? formData.startTime;
    const isRemote = overrides.isRemote ?? formData.isRemote;

    if (!claimantId || !appointmentDate || !startTime) {
      setDuplicateAppointmentWarning(null);
      return null;
    }
    if (!isRemote && !formData.serviceLocationId && !overrides.skipLocationCheck) {
      setDuplicateAppointmentWarning(null);
      return null;
    }

    try {
      const params = buildDuplicateCheckParams({ ...overrides, claimantId, appointmentDate, startTime, isRemote });
      const data = await makeAuthenticatedRequest(`/appointments/check-duplicate?${params.toString()}`);
      if (data.success && data.duplicate && data.appointment) {
        setDuplicateAppointmentWarning(data.appointment);
        if (data.appointment.id !== lastShownAppointmentDupRef.current) {
          lastShownAppointmentDupRef.current = data.appointment.id;
          showDuplicateAlert(setDuplicateModal, 'appointment', data.appointment);
        }
        return data.appointment;
      }
      setDuplicateAppointmentWarning(null);
      lastShownAppointmentDupRef.current = null;
      return null;
    } catch (error) {
      console.error('Error checking duplicate appointment:', error);
      return null;
    }
  }, [buildDuplicateCheckParams, formData.claimantId, formData.appointmentDate, formData.startTime, formData.isRemote, formData.serviceLocationId, isReRequest, makeAuthenticatedRequest]);

  const confirmDuplicateOrProceed = useCallback(async (overrides = {}) => {
    const duplicate = await checkDuplicateAppointment(overrides);
    return requestDuplicateConfirm(setDuplicateModal, 'appointment', duplicate);
  }, [checkDuplicateAppointment]);

  useEffect(() => {
    if (isReRequest || !formData.claimantId || !formData.appointmentDate || !formData.startTime) {
      setDuplicateAppointmentWarning(null);
      return undefined;
    }
    if (!formData.isRemote && !formData.serviceLocationId) {
      setDuplicateAppointmentWarning(null);
      return undefined;
    }
    const timer = setTimeout(() => {
      checkDuplicateAppointment();
    }, 400);
    return () => clearTimeout(timer);
  }, [
    isReRequest,
    formData.claimantId,
    formData.appointmentDate,
    formData.startTime,
    formData.isRemote,
    formData.serviceLocationId,
    formData.selectedServiceLocation,
    checkDuplicateAppointment
  ]);

  // Smart phone population: Check for similar previous requests
  const checkForSimilarRequests = useCallback(async (claimantId, claimId) => {
    if (!claimantId || !claimId || isReRequest) {
      console.log('Phone auto-population skipped:', { claimantId, claimId, isReRequest });
      return;
    }
    
    console.log('Checking for similar requests:', { claimantId, claimId });
    
    try {
      const data = await makeAuthenticatedRequest(`${API_BASE}/customer/appointments?claimantId=${claimantId}&claimId=${claimId}&limit=10`);
      
      console.log('API response for similar requests:', data);
      
      if (data.success && data.data.appointments && data.data.appointments.length > 0) {
        console.log('Found appointments:', data.data.appointments.length);
        
        // Find the most recent appointment with a phone number
        const recentAppointment = data.data.appointments.find(appointment => 
          appointment.phone && appointment.phone.trim() !== ''
        );
        
        console.log('Recent appointment with phone:', recentAppointment);
        console.log('Current phone field:', formData.phone);
        
        if (recentAppointment && !formData.phone) {
          console.log('Auto-populating phone:', recentAppointment.phone);
          
          // Auto-populate phone field
          setFormData(prev => ({
            ...prev,
            phone: recentAppointment.phone
          }));
          
          // Show a subtle notification
          toast.success(`Phone number auto-filled from previous request`, {
            duration: 3000,
            icon: '📞'
          });
        } else {
          console.log('No auto-population needed:', { recentAppointment: !!recentAppointment, currentPhone: formData.phone });
        }
      } else {
        console.log('No appointments found or API error');
      }
    } catch (error) {
      console.error('Error checking for similar requests:', error);
      // Don't show error to user as this is a background feature
    }
  }, [makeAuthenticatedRequest, isReRequest, formData.phone]);

  // Trigger smart phone population when claimant and claim are selected
  useEffect(() => {
    if (formData.claimantId && formData.claimId && !isReRequest) {
      checkForSimilarRequests(formData.claimantId, formData.claimId);
    }
  }, [formData.claimantId, formData.claimId, checkForSimilarRequests, isReRequest]);

  // Initialize Google Maps
  useEffect(() => {
    initializeGoogleMaps();
  }, []);

  // Setup address autocomplete when maps are initialized and ref is available
  useEffect(() => {
    if (mapsInitialized && locationAutocompleteRef.current) {
      console.log('Setting up autocomplete for ref:', locationAutocompleteRef.current);
      // Add a small delay to ensure the element is fully rendered
      const timer = setTimeout(async () => {
        await setupAddressAutocomplete(locationAutocompleteRef.current);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [mapsInitialized, locationAutocompleteRef.current]);

  // Cleanup autocomplete on unmount
  useEffect(() => {
    return () => {
      if (locationAutocompleteRef.current && locationAutocompleteRef.current._autocompleteInstance) {
        console.log('Cleaning up autocomplete instance');
        // Google Maps autocomplete doesn't have a direct cleanup method,
        // but we can remove the data attribute to allow re-initialization
        locationAutocompleteRef.current.removeAttribute('data-autocomplete-setup');
        delete locationAutocompleteRef.current._autocompleteInstance;
      }
    };
  }, []);

  const initializeGoogleMaps = async () => {
    console.log('Initializing Google Maps...');
    
    try {
      await googleMapsLoader.load(['places']);
      console.log('Google Maps API loaded successfully');
      setMapsInitialized(true);
    } catch (error) {
      console.error('Failed to load Google Maps API:', error);
    }
  };

  const setupAddressAutocomplete = async (inputElement) => {
    console.log('Setting up address autocomplete for element:', inputElement);
    
    try {
      // Wait for Google Maps and Places API to be fully loaded
      await googleMapsLoader.waitForLibraries(['places']);
      console.log('Google Maps Places API is now available');
    } catch (error) {
      console.error('Failed to load Google Maps Places API:', error);
      return;
    }

    // Check if autocomplete is already set up for this element
    if (inputElement.getAttribute('data-autocomplete-setup') === 'true') {
      console.log('Autocomplete already set up for this element');
      return;
    }

    console.log('Creating Google Places Autocomplete instance');
    const autocompleteInstance = new window.google.maps.places.Autocomplete(inputElement, {
      types: ['establishment', 'geocode'],
      componentRestrictions: { country: 'us' }
    });

    // Mark element as having autocomplete set up
    inputElement.setAttribute('data-autocomplete-setup', 'true');

    autocompleteInstance.addListener('place_changed', () => {
      console.log('Place changed event triggered');
      const place = autocompleteInstance.getPlace();
      console.log('Selected place:', place);
      
      if (place && place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        
        // Parse address components
        let streetNumber = '';
        let route = '';
        let city = '';
        let state = '';
        let zipCode = '';
        
        if (place.address_components) {
          place.address_components.forEach(component => {
            const types = component.types;
            if (types.includes('street_number')) {
              streetNumber = component.long_name;
            } else if (types.includes('route')) {
              route = component.long_name;
            } else if (types.includes('locality')) {
              city = component.long_name;
            } else if (types.includes('administrative_area_level_1')) {
              state = component.short_name;
            } else if (types.includes('postal_code')) {
              zipCode = component.long_name;
            }
          });
        }
        
        const fullAddress = place.formatted_address || `${streetNumber} ${route}`.trim();
        
        console.log('Updating form data with address:', {
          fullAddress,
          city,
          state,
          zipCode
        });
        
        setFormData(prev => ({
          ...prev,
          locationAddress: fullAddress,
          locationCity: city,
          locationState: state,
          locationZipCode: zipCode
        }));
      }
    });

    // Store the autocomplete instance for cleanup
    inputElement._autocompleteInstance = autocompleteInstance;
  };

  const setupClaimantAddressAutocomplete = async (inputElement) => {
    if (!inputElement) return;
    
    try {
      await googleMapsLoader.waitForLibraries(['places']);
    } catch (error) {
      console.error('Failed to load Google Maps Places API:', error);
      return;
    }

    // Prevent multiple initializations
    if (claimantAddressAutocompleteRef.current) {
      return;
    }

    const autocompleteInstance = new window.google.maps.places.Autocomplete(inputElement, {
      types: ['address'],
      componentRestrictions: { country: 'us' }
    });

    autocompleteInstance.addListener('place_changed', () => {
      const place = autocompleteInstance.getPlace();
      
      if (place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        
        setNewClaimantData(prev => ({
          ...prev,
          address: place.formatted_address || '',
          address_latitude: lat.toString(),
          address_longitude: lng.toString()
        }));
      }
    });

    claimantAddressAutocompleteRef.current = autocompleteInstance;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
      // Auto-set isRemote when service type is video or phone
      ...(field === 'serviceType' && (value === 'phone' || value === 'phone_conference' || value === 'video' || value === 'video_medical') ? { isRemote: true } : {}),
      ...(field === 'serviceType' && value !== 'phone' && value !== 'phone_conference' && value !== 'video' && value !== 'video_medical' ? { isRemote: false } : {})
    }));
    
    // Handle auto-population logic for interpreter type
    if ((field === 'appointmentType' || field === 'serviceType') && formOptions.interpreterTypes) {
      // Get the current values (using the new value if it's the field being changed)
      const currentAppointmentType = field === 'appointmentType' ? value : formData.appointmentType;
      const currentServiceType = field === 'serviceType' ? value : formData.serviceType;
      
      // Only auto-populate if service type is onsite
      if (currentServiceType === 'onsite' && currentAppointmentType) {
        const category = getAppointmentCategory(currentAppointmentType);
        let interpreterType = null;
        
        // Auto-populate for Legal appointments
        if (category === 'legal') {
          interpreterType = formOptions.interpreterTypes.find(it => it.code === 'court_certified');
          if (interpreterType) {
            setFormData(prev => ({
              ...prev,
              interpreterType: interpreterType.id.toString()
            }));
            toast.success(`Interpreter type automatically set to "${interpreterType.name}" for legal appointments`, {
              duration: 2000,
              position: 'top-right'
            });
          }
        }
        // Auto-populate for Medical Certified appointments
        else if (category === 'medical-legal') {
          interpreterType = formOptions.interpreterTypes.find(it => it.code === 'medical_certified');
          if (interpreterType) {
            setFormData(prev => ({
              ...prev,
              interpreterType: interpreterType.id.toString()
            }));
            toast.success(`Interpreter type automatically set to "${interpreterType.name}" for medical certified evaluations`, {
              duration: 2000,
              position: 'top-right'
            });
          }
        }
        // For Medical Standard, clear interpreter type so user can select
        else if (category === 'medical-standard') {
          setFormData(prev => ({
            ...prev,
            interpreterType: ''
          }));
        }
      }
      
      // Clear interpreter type if service type is not onsite
      if (field === 'serviceType' && value !== 'onsite') {
        setFormData(prev => ({
          ...prev,
          interpreterType: ''
        }));
      }
    }
  };

  const handleServiceLocationSelect = async (location) => {
    console.log('handleServiceLocationSelect called with:', location);
    if (location) {
      setFormData(prev => ({
        ...prev,
        selectedServiceLocation: location,
        serviceLocationId: location.id,
        locationAddress: location.address,
        locationCity: location.city || '',
        locationState: location.state || '',
        locationZipCode: location.zip_code || '',
        phone: location.phone_number || '',
        locationContactId: '' // Reset location contact when location changes
      }));
      
      // Fetch location contacts for this service location
      await loadLocationContacts(location.id);
    } else {
      setFormData(prev => ({
        ...prev,
        selectedServiceLocation: null,
        serviceLocationId: '',
        locationAddress: '',
        locationCity: '',
        locationState: '',
        locationZipCode: '',
        phone: '',
        locationContactId: '',
        locationContacts: []
      }));
    }
  };

  const loadLocationContacts = async (serviceLocationId) => {
    try {
      const response = await makeAuthenticatedRequest(`/location-contacts?service_location_id=${serviceLocationId}`);
      if (response.success) {
        setFormData(prev => ({
          ...prev,
          locationContacts: response.data || []
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          locationContacts: []
        }));
      }
    } catch (error) {
      console.error('Error loading location contacts:', error);
      setFormData(prev => ({
        ...prev,
        locationContacts: []
      }));
    }
  };

  const handleLocationContactSelect = (contactId) => {
    handleInputChange('locationContactId', contactId);
  };

  const [showNewContactModal, setShowNewContactModal] = useState(false);
  const [newContactType, setNewContactType] = useState('location_contact'); // 'customer' or 'location_contact'
  const [newContactFormData, setNewContactFormData] = useState({
    name: '',
    email: '',
    phone: '',
    title: ''
  });

  const handleCreateLocationContact = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event from bubbling to parent form
    
    if (!newContactFormData.name || !formData.serviceLocationId) {
      toast.error('Name and service location are required');
      return;
    }

    // Validate service_location_id is a valid integer
    const serviceLocationId = parseInt(formData.serviceLocationId);
    if (isNaN(serviceLocationId) || serviceLocationId <= 0) {
      toast.error('Invalid service location ID');
      return;
    }

    // Build request body - only include optional fields if they have values
    const requestBody = {
      service_location_id: serviceLocationId,
      name: newContactFormData.name.trim()
    };

    // Only add optional fields if they have values
    if (newContactFormData.email && newContactFormData.email.trim()) {
      requestBody.email = newContactFormData.email.trim();
    }
    if (newContactFormData.phone && newContactFormData.phone.trim()) {
      requestBody.phone = newContactFormData.phone.trim();
    }
    if (newContactFormData.title && newContactFormData.title.trim()) {
      requestBody.title = newContactFormData.title.trim();
    }

    try {
      const response = await makeAuthenticatedRequest('/location-contacts', {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      if (response.success) {
        toast.success('Location contact created successfully');
        setShowNewContactModal(false);
        setNewContactFormData({
          name: '',
          email: '',
          phone: '',
          title: ''
        });
        // Reload location contacts and auto-select the new one
        await loadLocationContacts(formData.serviceLocationId);
        handleInputChange('locationContactId', response.data.id.toString());
      } else {
        toast.error(response.message || 'Failed to create location contact');
      }
    } catch (error) {
      console.error('Error creating location contact:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to create location contact'));
    }
  };

  // Helper function to handle reserve hours input with validation
  const handleReserveHoursChange = (value) => {
    const minHours = getMinimumReserveHours(
      resolveReserveServiceTypeKey(formData, formOptions)
    );
    const numValue = parseFloat(value);
    if (isNaN(numValue) || value === '') {
      handleInputChange('reserveHours', '');
    } else {
      const roundedValue = Math.round(numValue * 2) / 2;
      handleInputChange('reserveHours', Math.max(roundedValue, minHours));
    }
  };

  const openAddClaimModal = (prefillClaimNumber = '') => {
    setNewClaimData({
      ...initialNewClaimState,
      claimantId: formData.claimantId || '',
      claimNumber: prefillClaimNumber || '',
      contactClaimsHandlerId: customer?.id ? customer.id.toString() : ''
    });
    setShowAddClaimModal(true);
  };

  const closeAddClaimModal = () => {
    setShowAddClaimModal(false);
    setIsSavingClaim(false);
    setNewClaimData(initialNewClaimState);
    setNewClaimNumberWarning(null);
    setNewClaimantNameWarning(null);
  };

  const handleNewClaimFieldChange = (field, value) => {
    setNewClaimData(prev => ({
      ...prev,
      [field]: value
    }));
    if (field === 'claimNumber') setNewClaimNumberWarning(null);
  };

  const handleNewClaimantFieldChange = (field, value) => {
    setNewClaimantData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'firstName' || field === 'lastName') {
        newClaimantNamesRef.current = {
          firstName: next.firstName,
          lastName: next.lastName
        };
      }
      return next;
    });
    if (field === 'firstName' || field === 'lastName') {
      setNewClaimantNameWarning(null);
    }
  };

  const checkNewClaimNumberOnBlur = async () => {
    const num = (newClaimData.claimNumber || '').trim();
    if (!num) { setNewClaimNumberWarning(null); return; }
    const claim = await fetchClaimNumberDuplicate(makeAuthenticatedRequest, num);
    setNewClaimNumberWarning(claim);
    showDuplicateAlert(setDuplicateModal, 'claim', claim);
  };

  const checkNewClaimantNameOnBlur = async (firstName, lastName) => {
    await runClaimantNameDuplicateCheck(
      makeAuthenticatedRequest,
      setDuplicateModal,
      setNewClaimantNameWarning,
      firstName,
      lastName
    );
  };

  const handleCreateNewClaim = async (e) => {
    e.preventDefault();
    if (!newClaimData.claimNumber.trim()) {
      toast.error('Claim number is required');
      return;
    }
    if (!newClaimData.caseType) {
      toast.error('Case type is required');
      return;
    }
    if (!newClaimData.contactClaimsHandlerId || !newClaimData.adjustersAssistantId) {
      toast.error('Contact/claims handler and adjuster\'s assistant are required');
      return;
    }

    const claimantIdForClaim = newClaimData.claimantId || formData.claimantId || '';
    if (!claimantIdForClaim) {
      toast.error('Please select a claimant before creating a claim');
      return;
    }

    const payload = {
      case_type: newClaimData.caseType,
      claim_number: newClaimData.claimNumber.trim(),
      date_of_injury: newClaimData.dateOfInjury || null,
      ct_start_date: newClaimData.ctStartDate || null,
      ct_end_date: newClaimData.ctEndDate || null,
      diagnosis: newClaimData.diagnosis || null,
      employer_insured: newClaimData.employer || null,
      contact_claims_handler_id: newClaimData.contactClaimsHandlerId || null,
      adjusters_assistant_id: newClaimData.adjustersAssistantId || null,
      nurse_case_manager_id: newClaimData.nurseCaseManagerId || null,
      field_case_manager_id: newClaimData.fieldCaseManagerId || null,
      claimant_id: claimantIdForClaim ? parseInt(claimantIdForClaim, 10) : null
    };

    setIsSavingClaim(true);
    try {
      const claimDup = await fetchClaimNumberDuplicate(makeAuthenticatedRequest, newClaimData.claimNumber);
      setNewClaimNumberWarning(claimDup);
      if (!(await requestDuplicateConfirm(setDuplicateModal, 'claim', claimDup))) {
        setIsSavingClaim(false);
        return;
      }

      const response = await makeAuthenticatedRequest('/claims', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (!response.success || !response.data) {
        toast.error(response.message || 'Failed to create claim');
        setIsSavingClaim(false);
        return;
      }

      const createdClaim = response.data;
      toast.success('Claim created successfully');

      setAllClaims(prev => {
        const withoutDuplicate = prev.filter(claim => claim.id !== createdClaim.id);
        return [createdClaim, ...withoutDuplicate];
      });

      const createdClaimantId = createdClaim.claimant_id ? createdClaim.claimant_id.toString() : '';

      if (createdClaimantId) {
        if (formData.claimantId !== createdClaimantId) {
          await handleClaimantChange(createdClaimantId, true);
        } else {
          await loadClaimsForClaimant(createdClaimantId);
        }
      }

      const claimIdString = createdClaim.id.toString();
      selectClaimById(claimIdString, [createdClaim]);
      setFormData(prev => ({
        ...prev,
        claimId: claimIdString,
        claimantId: createdClaimantId || prev.claimantId
      }));

      closeAddClaimModal();
    } catch (error) {
      console.error('Error creating claim:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to create claim'));
    } finally {
      setIsSavingClaim(false);
    }
  };

  const openAddClaimantModal = (prefillName = '') => {
    const trimmed = prefillName.trim();
    let derivedFirstName = trimmed || formData.claimantFirstName || '';
    let derivedLastName = formData.claimantLastName || '';

    if (trimmed.includes(' ')) {
      const parts = trimmed.split(' ').filter(Boolean);
      derivedFirstName = parts.shift() || derivedFirstName;
      const rest = parts.join(' ');
      derivedLastName = rest || derivedLastName;
    } else if (trimmed && !formData.claimantLastName) {
      derivedLastName = '';
    }

    setNewClaimantData({
      ...initialNewClaimantState,
      firstName: derivedFirstName,
      lastName: derivedLastName,
      phone: formData.claimantPhone || '',
      language: formData.claimantLanguage || '',
      dateOfBirth: formData.claimantDateOfBirth || ''
    });
    setShowAddClaimantModal(true);
  };

  useEffect(() => {
    if (!showAddClaimantModal) return;

    const fn = (newClaimantNamesRef.current.firstName || newClaimantData.firstName || '').trim();
    const ln = (newClaimantNamesRef.current.lastName || newClaimantData.lastName || '').trim();
    if (!fn || !ln) return;

    const timer = setTimeout(() => {
      checkNewClaimantNameOnBlur(fn, ln);
    }, 500);

    return () => clearTimeout(timer);
  }, [newClaimantData.firstName, newClaimantData.lastName, showAddClaimantModal]);

  const closeAddClaimantModal = () => {
    setShowAddClaimantModal(false);
    setIsSavingClaimant(false);
    setNewClaimantData(initialNewClaimantState);
    setNewClaimantNameWarning(null);
    newClaimantNamesRef.current = { firstName: '', lastName: '' };
    // Reset autocomplete ref when modal closes
    if (claimantAddressAutocompleteRef.current) {
      claimantAddressAutocompleteRef.current = null;
    }
  };

  const handleCreateNewClaimant = async (e) => {
    e.preventDefault();
    if (!newClaimantData.firstName.trim() || !newClaimantData.lastName.trim()) {
      toast.error('First and last name are required');
      return;
    }

    const payload = {
      first_name: newClaimantData.firstName.trim(),
      last_name: newClaimantData.lastName.trim(),
      phone: newClaimantData.phone ? newClaimantData.phone.trim() : '',
      date_of_birth: newClaimantData.dateOfBirth || '',
      language: newClaimantData.language === 'other' ? newClaimantData.customLanguage : (newClaimantData.language || ''),
      gender: newClaimantData.gender || '',
      address: newClaimantData.address || '',
      address_latitude: newClaimantData.address_latitude || null,
      address_longitude: newClaimantData.address_longitude || null
    };

    setIsSavingClaimant(true);
    try {
      const claimantDup = await fetchClaimantNameDuplicate(
        makeAuthenticatedRequest,
        newClaimantData.firstName,
        newClaimantData.lastName
      );
      setNewClaimantNameWarning(claimantDup);
      if (!(await requestDuplicateConfirm(setDuplicateModal, 'claimant', claimantDup))) {
        setIsSavingClaimant(false);
        return;
      }

      const response = await makeAuthenticatedRequest('/claimants', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (!response.success || !response.data) {
        toast.error(response.message || 'Failed to create claimant');
        setIsSavingClaimant(false);
        return;
      }

      const createdClaimant = response.data;
      toast.success('Claimant created successfully');
      setNewClaimantNameWarning(null);

      setFormOptions(prev => ({
        ...prev,
        claimants: [createdClaimant, ...(prev.claimants || [])]
      }));

      await handleClaimantChange(createdClaimant.id.toString());
      setFormData(prev => ({
        ...prev,
        claimantId: createdClaimant.id.toString()
      }));

      closeAddClaimantModal();
      
      // Prompt user to create a claim for the new claimant
      toast.success('Claimant created successfully. Please create a claim for this claimant.', {
        duration: 5000
      });
      
      // Open the claim creation modal
      setTimeout(() => {
        setNewClaimData(prev => ({
          ...prev,
          claimantId: createdClaimant.id.toString()
        }));
        setShowAddClaimModal(true);
      }, 500);
    } catch (error) {
      console.error('Error creating claimant:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to create claimant'));
    } finally {
      setIsSavingClaimant(false);
    }
  };

  const handleClaimantChange = useCallback((claimantId, preserveClaimId = false) => {
    return new Promise((resolve) => {
      if (!claimantId) {
        setFormData(prev => ({
          ...prev,
          claimantId: '',
          claimId: '',
          claimantFirstName: '',
          claimantLastName: '',
          claimantGender: '',
          claimantDateOfBirth: '',
          claimantPhone: '',
          claimantLanguage: '',
          claimantAddress: '',
          claimantAddressLatitude: '',
          claimantAddressLongitude: '',
          claimantEmployerInsured: ''
        }));
        resolve();
        return;
      }

      const selectedClaimant = (formOptions.claimants || []).find(c => c.id === parseInt(claimantId));
      if (selectedClaimant) {
        // Find matching language in form options if claimant has a language
        let autoSelectedLanguage = '';
        if (selectedClaimant.language && formOptions.languages) {
          const matchingLanguage = formOptions.languages.find(lang => 
            lang.name.toLowerCase() === selectedClaimant.language.toLowerCase()
          );
          if (matchingLanguage) {
            autoSelectedLanguage = matchingLanguage.id.toString();
          }
        }

        setFormData(prev => ({
          ...prev,
          claimantId: claimantId,
          claimId: preserveClaimId ? prev.claimId : '',
          claimantFirstName: selectedClaimant.first_name || '',
          claimantLastName: selectedClaimant.last_name || '',
          claimantGender: selectedClaimant.gender || '',
          claimantDateOfBirth: selectedClaimant.date_of_birth || '',
          claimantPhone: selectedClaimant.phone || '',
          claimantLanguage: selectedClaimant.language || '',
          claimantAddress: selectedClaimant.address || '',
          claimantAddressLatitude: selectedClaimant.address_latitude || '',
          claimantAddressLongitude: selectedClaimant.address_longitude || '',
          claimantEmployerInsured: selectedClaimant.employer_insured || '',
          // Auto-populate language if claimant has one and it matches available options
          language: autoSelectedLanguage || prev.language
        }));

        // Show toast notification if language was auto-populated
        if (autoSelectedLanguage && selectedClaimant.language) {
          toast.success(`Language auto-selected: ${selectedClaimant.language}`, { 
            duration: 3000, 
            icon: '🌐' 
          });
        }
        
        loadClaimsForClaimant(claimantId).then(() => {
          resolve();
        });
      } else {
        resolve();
      }
    });
  }, [formOptions.claimants, loadClaimsForClaimant]);

  // Helper to select a claim from a provided claims list (avoids relying on async state)
  const selectClaimById = (claimId, claimsList) => {
    if (!claimId) return;
    const selectedClaim = (claimsList || []).find(c => c.id === parseInt(claimId));
    if (!selectedClaim) return;

    const currentCustomerId = customer?.id;
    const isAssigned = currentCustomerId && (
      selectedClaim.contact_claims_handler_id === currentCustomerId ||
      selectedClaim.adjusters_assistant_id === currentCustomerId
    );
    if (!isAssigned) {
      const proceed = window.confirm(
        'Our records show that you that you are not assigned to this claim. To proceed, please press continue.'
      );
      if (!proceed) {
        setFormData(prev => ({ ...prev, claimId: '' }));
        return;
      }
    }

    const formatDateForInput = (dateValue) => {
      if (!dateValue) return '';
      if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
      }
      try {
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return '';
        return date.toISOString().split('T')[0];
      } catch (error) {
        console.error('Error formatting date:', error);
        return '';
      }
    };

    setFormData(prev => {
      const updates = {
        claimId: claimId,
        dateOfInjury: formatDateForInput(selectedClaim.date_of_injury) || prev.dateOfInjury,
        employer: selectedClaim.employer || prev.employer
      };

      // Populate claimant date of birth and phone if available from claim
      if (selectedClaim.claimant_date_of_birth && !prev.claimantDateOfBirth) {
        updates.claimantDateOfBirth = formatDateForInput(selectedClaim.claimant_date_of_birth);
      }
      if (selectedClaim.claimant_phone && !prev.claimantPhone) {
        updates.claimantPhone = selectedClaim.claimant_phone;
      }

      // Auto-populate language from claim if available
      if (selectedClaim.claimant_language && formOptions.languages && formOptions.languages.length > 0) {
        const matchingLanguage = formOptions.languages.find(lang => 
          lang.name.toLowerCase() === selectedClaim.claimant_language.toLowerCase()
        );
        if (matchingLanguage) {
          updates.language = matchingLanguage.id.toString();
          toast.success(`Language auto-populated: ${matchingLanguage.name}`, {
            duration: 3000
          });
        }
      }

      return {
        ...prev,
        ...updates
      };
    });
  };

  const handleClaimChange = async (claimId) => {
    if (!claimId) {
      setFormData(prev => ({
        ...prev,
        claimId: ''
      }));
      return;
    }

    // If we don't have a claimant selected, search in allClaims
    if (!formData.claimantId) {
      const selectedClaim = (allClaims || []).find(c => c.id.toString() === claimId);
      if (selectedClaim) {
        // Set claimant and then load their claims (only if claimant_id exists)
        if (selectedClaim.claimant_id) {
          const claimantName = selectedClaim.claimant_first_name && selectedClaim.claimant_last_name 
            ? `${selectedClaim.claimant_first_name} ${selectedClaim.claimant_last_name}`
            : selectedClaim.claimant_name;
          
          // First, set the claim in the form data
          selectClaimById(claimId, [selectedClaim]);
          
          toast.success(`Claimant auto-populated: ${claimantName}`, {
            duration: 3000
          });
          
          // Then auto-populate the claimant, preserving the claim selection
          await handleClaimantChange(selectedClaim.claimant_id.toString(), true);
        } else {
          // Claim is missing claimant_id link, but still populate the claim
          console.warn('Selected claim has no claimant_id:', selectedClaim);
          toast.error('Please manually select the claimant for this claim', {
            duration: 4000
          });
          
          // Still populate the claim information
          selectClaimById(claimId, [selectedClaim]);
        }
      }
    } else {
      // Use current state claims list for claimant-specific claims
      selectClaimById(claimId, formOptions.claims || []);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not scheduled';
    
    try {
      // Extract date parts to avoid timezone conversion
      const datePart = dateString.split('T')[0];
      const [year, month, day] = datePart.split('-').map(num => parseInt(num, 10));
      
      // Create date in local timezone (no UTC conversion)
      const localDate = new Date(year, month - 1, day);
      
      if (isNaN(localDate.getTime())) {
        return 'Invalid Date';
      }
      
      return localDate.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  const clearClaimantSelection = () => {
    setFormData(prev => ({
      ...prev,
      claimantId: '',
      claimId: '',
      claimantFirstName: '',
      claimantLastName: '',
      claimantGender: '',
      claimantDateOfBirth: '',
      claimantPhone: '',
      claimantLanguage: '',
      claimantAddress: '',
      claimantAddressLatitude: '',
      claimantAddressLongitude: '',
      claimantEmployerInsured: '',
      dateOfInjury: '',
      employer: ''
    }));
    setFormOptions(prev => ({ ...prev, claims: [] }));
  };

  // Step validation functions with error messages
  const validateStep = (step) => {
    switch (step) {
      case 1: // Job Type
        if (jobType !== 'interpreting') {
          toast.error('Please select Interpreting Service to continue');
          return false;
        }
        return true;
      case 2: // Claim Info
        if (!formData.claimantId) {
          toast.error('Please select a claimant to continue');
          return false;
        }
        if (!formData.claimId) {
          toast.error('Please select a claim to continue');
          return false;
        }
        return true;
      case 3: // Appointment Details
        if (formData.isRepeatedAppointment) {
          if (!formData.repeatedAppointments || formData.repeatedAppointments.length === 0) {
            toast.error('Please add at least one appointment date and time');
            return false;
          }
          // Validate each repeated appointment
          for (let i = 0; i < formData.repeatedAppointments.length; i++) {
            const appointment = formData.repeatedAppointments[i];
            if (!appointment.date) {
              toast.error(`Please select a date for appointment ${i + 1}`);
              return false;
            }
            if (!appointment.time) {
              toast.error(`Please enter a start time for appointment ${i + 1}`);
              return false;
            }
          }
        } else {
          if (!formData.appointmentDate) {
            toast.error('Please select an appointment date');
            return false;
          }
          if (!formData.startTime) {
            toast.error('Please enter a start time');
            return false;
          }
        }
        if (!formData.appointmentType) {
          toast.error('Please select an appointment type');
          return false;
        }
        if (formData.appointmentType === 'other' && !formData.customAppointmentType) {
          toast.error('Please specify the custom appointment type');
          return false;
        }
        return true;
      case 4: // Interpreter Requirements
        if (!formData.language) {
          toast.error('Please select a language');
          return false;
        }
        if (formData.language === 'other' && !formData.customLanguage) {
          toast.error('Please enter the language');
          return false;
        }
        if (!formData.serviceType) {
          toast.error('Please select a service type');
          return false;
        }
        // Interpreter type is required for:
        // - Video and phone service types (always required)
        // - Onsite medical-standard appointments
        const appointmentCategory = formData.appointmentType ? getAppointmentCategory(formData.appointmentType) : null;
        const isVideoOrPhone = formData.serviceType === 'video' || formData.serviceType === 'video_medical' || formData.serviceType === 'phone' || formData.serviceType === 'phone_conference';
        const isOnsiteMedicalStandard = formData.serviceType === 'onsite' && appointmentCategory === 'medical-standard';
        const needsInterpreterType = isVideoOrPhone || isOnsiteMedicalStandard;
        
        if (needsInterpreterType && !formData.interpreterType) {
          toast.error('Please select an interpreter type');
          return false;
        }
        return true;
      case 5: // Location
        if (interpretingJobServiceLocationOptional(formData)) {
          if (formData.serviceLocationId && !formData.locationContactId) {
            toast.error('Please select a location contact for this service location');
            return false;
          }
          return true;
        }
        if (!formData.selectedServiceLocation || !formData.serviceLocationId) {
          toast.error('Please select a service location');
          return false;
        }
        if (!formData.locationContactId) {
          toast.error('Please select a location contact for this service location');
          return false;
        }
        return true;
      case 6: // PDF Attachment (optional)
        return true; // Always valid since attachment is optional
      default:
        return false;
    }
  };

  const markStepCompleted = (step) => {
    setCompletedSteps(prev => new Set([...prev, step]));
  };

  const goToNextStep = () => {
    if (validateStep(currentStep)) {
      markStepCompleted(currentStep);
      if (currentStep < totalSteps) {
        let nextStep = currentStep + 1;
        // Skip Location step (5) for phone-only remote jobs
        if (nextStep === 5 && shouldSkipLocationStep(formData.serviceType)) {
          markStepCompleted(5);
          nextStep = 6;
        }
        setCurrentStep(nextStep);
      }
    }
    // Error message is now shown in validateStep
  };

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      let prevStep = currentStep - 1;
      if (prevStep === 5 && shouldSkipLocationStep(formData.serviceType)) {
        prevStep = 4;
      }
      setCurrentStep(prevStep);
    }
  };

  const goToStep = (step) => {
    if (step === 5 && shouldSkipLocationStep(formData.serviceType)) {
      return;
    }
    // Allow going to any completed step or the next step
    if (step <= currentStep || completedSteps.has(step - 1)) {
      setCurrentStep(step);
    }
  };

  const handleStep1PrimaryAction = () => {
    if (!jobType) {
      toast.error('Please select a service type to continue');
      return;
    }

    if (jobType === 'transportation') {
      const params = new URLSearchParams();
      const claimId = searchParams?.get('claimId');
      const claimantId = searchParams?.get('claimantId');
      if (claimId) params.set('claimId', claimId);
      if (claimantId) params.set('claimantId', claimantId);
      const queryString = params.toString();
      navigate(`/transportation/new${queryString ? `?${queryString}` : ''}`);
      return;
    }

    setShowServiceTypeSelection(true);
  };

  const handleCancelSearch = () => {
    // Clear the interval
    if (searchInterval) {
      clearInterval(searchInterval);
      setSearchInterval(null);
    }
    
    // Hide animation and navigate to specific appointment details
    setShowSearchAnimation(false);
    toast('Search cancelled. You can check your appointment details for updates.');
    if (appointmentId) {
      navigate(`/appointments/${appointmentId}`);
    } else {
      navigate('/appointments');
    }
  };

  const handleCloseSearch = () => {
    // Just hide the animation without navigating away
    setShowSearchAnimation(false);
    toast('Search is continuing in the background. You can check your appointment details for updates.');
    if (appointmentId) {
      navigate(`/appointments/${appointmentId}`);
    }
  };

  const checkAppointmentStatus = async (appointmentId) => {
    try {
      const data = await makeAuthenticatedRequest(`/appointments/${appointmentId}`);
      
      if (data.success) {
        const appointment = data.data;
        
        // If interpreter is assigned, hide animation and navigate to appointment details
        if (appointment.status === 'assigned' || appointment.status === 'reminders_sent' || 
            appointment.status === 'in_progress' || appointment.status === 'completed' ||
            appointment.status === 'completion_report' || appointment.status === 'billed' ||
            appointment.status === 'closed' || appointment.status === 'interpreter_paid') {
          
          // Clear the interval
          if (searchInterval) {
            clearInterval(searchInterval);
            setSearchInterval(null);
          }
          
          // Hide animation and navigate to specific appointment details
          setShowSearchAnimation(false);
          toast.success('Interpreter found! Your appointment has been assigned.');
          navigate(`/appointments/${appointmentId}`);
        }
      }
    } catch (error) {
      console.error('Error checking appointment status:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Apply defaults (blank reserve hours uses service-type minimum: 2h standard, 3h legal/video)
      const reserveServiceTypeKey = resolveReserveServiceTypeKey(formData, formOptions);
      const finalReserveHours = resolveReserveHours(formData.reserveHours, reserveServiceTypeKey);
      const finalArrivalTime = formData.arrivalTime || formData.startTime;
      const finalAppointmentType = formData.appointmentType === 'other' 
        ? formData.customAppointmentType 
        : formData.appointmentType;

      // Validate required fields
      if (formData.isRepeatedAppointment) {
        if (!formData.repeatedAppointments || formData.repeatedAppointments.length === 0) {
          toast.error('Please add at least one appointment date and time');
          setLoading(false);
          return;
        }
        // Validate each repeated appointment
        for (let i = 0; i < formData.repeatedAppointments.length; i++) {
          const appointment = formData.repeatedAppointments[i];
          if (!appointment.date) {
            toast.error(`Please select a date for appointment ${i + 1}`);
            setLoading(false);
            return;
          }
          if (!appointment.time) {
            toast.error(`Please enter a start time for appointment ${i + 1}`);
            setLoading(false);
            return;
          }
          // Validate date is not in the past
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const [year, month, day] = appointment.date.split('-').map(Number);
          const selectedDate = new Date(year, month - 1, day);
          selectedDate.setHours(0, 0, 0, 0);
          if (selectedDate < today) {
            toast.error(`Appointment ${i + 1} date cannot be in the past`);
            setLoading(false);
            return;
          }
        }
      } else {
        if (!formData.appointmentDate) {
          toast.error('Please select an appointment date');
          setLoading(false);
          return;
        }
        if (!formData.startTime) {
          toast.error('Please enter a start time');
          setLoading(false);
          return;
        }
        // Validate that appointment date is not in the past
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const [year, month, day] = formData.appointmentDate.split('-').map(Number);
        const selectedDate = new Date(year, month - 1, day);
        selectedDate.setHours(0, 0, 0, 0);
        if (selectedDate < today) {
          toast.error('Appointment date cannot be in the past');
          setLoading(false);
          return;
        }
      }
      if (!finalAppointmentType) {
        toast.error('Please select or specify an appointment type');
        setLoading(false);
        return;
      }
      if (!formData.claimantId) {
        toast.error('Please select a claimant');
        setLoading(false);
        return;
      }
      if (!formData.claimId) {
        toast.error('Please select a claim');
        setLoading(false);
        return;
      }
      if (!formData.serviceType) {
        toast.error('Please select a service type');
        setLoading(false);
        return;
      }
      // Interpreter type is required for video, video_medical, phone, and onsite medical-standard appointments
      const appointmentCategory = formData.appointmentType ? getAppointmentCategory(formData.appointmentType) : null;
      const isVideoOrPhoneSubmit = formData.serviceType === 'video' || formData.serviceType === 'video_medical' || formData.serviceType === 'phone' || formData.serviceType === 'phone_conference';
      const isOnsiteMedicalStandardSubmit = formData.serviceType === 'onsite' && appointmentCategory === 'medical-standard';
      const needsInterpreterType = isVideoOrPhoneSubmit || isOnsiteMedicalStandardSubmit;
      if (needsInterpreterType && !formData.interpreterType) {
        toast.error('Please select an interpreter type');
        setLoading(false);
        return;
      }
      if (!formData.language) {
        toast.error('Please select a language');
        setLoading(false);
        return;
      }
      if (formData.language === 'other' && !formData.customLanguage) {
        toast.error('Please enter the language');
        setLoading(false);
        return;
      }
      if (!formData.isRepeatedAppointment && !finalArrivalTime) {
        toast.error('Arrival time could not be determined. Please enter a start time.');
        setLoading(false);
        return;
      }

      // Validate phone field for non-remote appointments
      if (!formData.isRemote && !formData.phone) {
        toast.error('Phone number is required for in-person appointments');
        return;
      }

      // Validate reserve hours (skip for document service type which uses word count instead)
      if (formData.serviceType !== 'document') {
        const minReserveHours = getMinimumReserveHours(reserveServiceTypeKey);
        if (finalReserveHours < minReserveHours) {
          toast.error(`Reserve hours must be at least ${minReserveHours} hours for this service type`);
          setLoading(false);
          return;
        }
        
        if (finalReserveHours > 12) {
          toast.error('Reserve hours cannot exceed 12 hours');
          setLoading(false);
          return;
        }
      }

      if (!interpretingJobServiceLocationOptional(formData)) {
        if (!formData.selectedServiceLocation || !formData.serviceLocationId) {
          toast.error('Please select a service location for in-person appointments');
          return;
        }
        if (!formData.locationContactId) {
          toast.error('Please select a location contact for this service location');
          return;
        }
      } else if (formData.serviceLocationId && !formData.locationContactId) {
        toast.error('Please select a location contact for this service location');
        setLoading(false);
        return;
      }


      // Helper function to create appointment FormData
      const createAppointmentFormData = (appointmentDate, startTime, arrivalTime) => {
        const formDataToSend = new FormData();
        
        // Add all form fields to FormData (using final values with defaults applied)
        formDataToSend.append('appointmentDate', appointmentDate);
        formDataToSend.append('startTime', startTime);
        formDataToSend.append('endTime', calculateEndTime(startTime, finalReserveHours));
        formDataToSend.append('arrivalTime', arrivalTime || startTime);
        formDataToSend.append('appointmentType', finalAppointmentType);
        formDataToSend.append('locationContactId', formData.locationContactId ? parseInt(formData.locationContactId) : '');
        formDataToSend.append('isRemote', formData.isRemote);
        formDataToSend.append('locationAddress', formData.locationAddress);
        formDataToSend.append('locationCity', formData.locationCity);
        formDataToSend.append('locationState', formData.locationState);
        formDataToSend.append('locationZipCode', formData.locationZipCode);
        formDataToSend.append('phone', formData.phone);
        formDataToSend.append('serviceLocationId', formData.serviceLocationId ? parseInt(formData.serviceLocationId) : '');
        formDataToSend.append('claimantId', parseInt(formData.claimantId));
        formDataToSend.append('claimId', formData.claimId ? parseInt(formData.claimId) : '');
        
        // Map frontend service type to backend service_type_id
        const interpreterTypeCode = formData.interpreterType ? 
          formOptions.interpreterTypes?.find(it => it.id.toString() === formData.interpreterType)?.code : null;
        const backendServiceTypeId = mapToBackendServiceType(
          formData.serviceType, 
          formData.appointmentType, 
          interpreterTypeCode, 
          formOptions
        );
        formDataToSend.append('serviceTypeId', backendServiceTypeId ? parseInt(backendServiceTypeId) : '');
        
        // Handle language: if "other" is selected, use customLanguage; otherwise use language ID
        const languageToSend = formData.language === 'other' ? formData.customLanguage : formData.language;
        formDataToSend.append('language', languageToSend);
        formDataToSend.append('interpreterType', formData.interpreterType);
        formDataToSend.append('specialRequirements', formData.specialRequirements);
        formDataToSend.append('appointmentNotes', formData.appointmentNotes);
        formDataToSend.append('meetingLink', formData.meetingLink || '');
        
        // Add estimated word count for document translation jobs
        if (formData.serviceType === 'document' && formData.estimatedWordCount) {
          formDataToSend.append('estimatedWordCount', parseInt(formData.estimatedWordCount));
        }
        
        // Add PDF attachment if provided
        if (formData.attachment) {
          formDataToSend.append('attachment', formData.attachment);
        }
        
        return formDataToSend;
      };

      // Create appointments
      if (formData.isRepeatedAppointment) {
        // Create multiple appointments
        const appointmentsToCreate = formData.repeatedAppointments.map(appt => ({
          date: appt.date,
          time: appt.time,
          arrivalTime: appt.arrivalTime || appt.time
        }));

        for (let i = 0; i < appointmentsToCreate.length; i++) {
          const appointment = appointmentsToCreate[i];
          const proceed = await confirmDuplicateOrProceed({
            appointmentDate: appointment.date,
            startTime: appointment.time
          });
          if (!proceed) {
            setLoading(false);
            return;
          }
        }

        let successCount = 0;
        let failCount = 0;
        const createdAppointmentIds = [];
        let firstAppointmentId = null; // Store the first appointment ID to link others to it

        for (let i = 0; i < appointmentsToCreate.length; i++) {
          const appointment = appointmentsToCreate[i];
          try {
            const formDataToSend = createAppointmentFormData(
              appointment.date,
              appointment.time,
              appointment.arrivalTime
            );
            
            // Link to the first appointment if this is not the first one
            if (firstAppointmentId) {
              formDataToSend.append('relatedJobId', firstAppointmentId);
            }

            const data = await makeAuthenticatedRequest('/appointments/simple', {
              method: 'POST',
              body: formDataToSend,
            });

            if (data.success) {
              successCount++;
              createdAppointmentIds.push(data.data.id);
              // Store the first appointment ID for linking subsequent appointments
              if (!firstAppointmentId) {
                firstAppointmentId = data.data.id;
              }
            } else {
              failCount++;
              console.error(`Failed to create appointment ${i + 1}:`, data.message);
              if (data.duplicate && data.appointment) {
                setDuplicateAppointmentWarning(data.appointment);
              }
            }
          } catch (error) {
            failCount++;
            console.error(`Error creating appointment ${i + 1}:`, error);
            if (error.apiResponse?.duplicate && error.apiResponse?.appointment) {
              setDuplicateAppointmentWarning(error.apiResponse.appointment);
            }
            toast.error(
              getCustomerPortalErrorMessage(error, `Failed to create appointment ${i + 1}`)
            );
          }
        }

        if (successCount > 0) {
          toast.success(`Successfully created ${successCount} appointment${successCount > 1 ? 's' : ''}!`);
          if (failCount > 0) {
            toast.error(`Failed to create ${failCount} appointment${failCount > 1 ? 's' : ''}`);
          }
          
          // Navigate to the first created appointment
          if (createdAppointmentIds.length > 0) {
            setShowSearchAnimation(true);
            setAppointmentId(createdAppointmentIds[0]);
            setSearchStartTime(Date.now());
            
            // Start polling for appointment status every 2 seconds
            const interval = setInterval(() => {
              checkAppointmentStatus(createdAppointmentIds[0]);
            }, 2000);
            setSearchInterval(interval);
            
            // Set a maximum timeout of 5 minutes to prevent infinite polling
            setTimeout(() => {
              if (searchInterval) {
                clearInterval(searchInterval);
                setSearchInterval(null);
              }
              setShowSearchAnimation(false);
              toast('Search is taking longer than expected. You can check your appointment details for updates.');
              navigate(`/appointments/${createdAppointmentIds[0]}`);
            }, 300000); // 5 minutes
          } else {
            navigate('/appointments');
          }
        } else {
          toast.error('Failed to create appointments. Please try again.');
        }
      } else {
        const proceed = await confirmDuplicateOrProceed();
        if (!proceed) {
          setLoading(false);
          return;
        }

        // Create single appointment
        const formDataToSend = createAppointmentFormData(
          formData.appointmentDate,
          formData.startTime,
          finalArrivalTime
        );

        const data = await makeAuthenticatedRequest('/appointments/simple', {
          method: 'POST',
          body: formDataToSend,
          // Don't set Content-Type header - let the browser set it with boundary for FormData
        });

        if (data.success) {
          toast.success('Appointment request submitted successfully!');
          setShowSearchAnimation(true);
          setAppointmentId(data.data.id);
          setSearchStartTime(Date.now());
          
          // Start polling for appointment status every 2 seconds
          const interval = setInterval(() => {
            checkAppointmentStatus(data.data.id);
          }, 2000);
          setSearchInterval(interval);
          
          // Set a maximum timeout of 5 minutes to prevent infinite polling
          setTimeout(() => {
            if (searchInterval) {
              clearInterval(searchInterval);
              setSearchInterval(null);
            }
            setShowSearchAnimation(false);
            toast('Search is taking longer than expected. You can check your appointment details for updates.');
            navigate(`/appointments/${data.data.id}`);
          }, 300000); // 5 minutes
        } else {
          if (data.duplicate && data.appointment) {
            setDuplicateAppointmentWarning(data.appointment);
          }
          toast.error(data.message || 'Failed to submit appointment request');
        }
      }
    } catch (error) {
      console.error('Error submitting appointment:', error);
      if (error.apiResponse?.duplicate && error.apiResponse?.appointment) {
        setDuplicateAppointmentWarning(error.apiResponse.appointment);
      }
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to submit appointment request'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-4xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center">
            <button
              onClick={() => {
                // Check for returnTo parameter first (explicit return path)
                const returnTo = searchParams.get('returnTo');
                if (returnTo) {
                  navigate(returnTo);
                  return;
                }
                
                // If we came from a re-request, go back to that appointment
                const reRequestId = searchParams.get('reRequest');
                if (reRequestId) {
                  navigate(`/appointments/${reRequestId}`);
                  return;
                }
                
                // Otherwise, use proper back navigation to the previous page
                navigateBack(navigate, location.pathname, '/appointments');
              }}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
            >
              <ArrowLeftIcon className="h-6 w-6" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <PlusIcon className="h-8 w-8 mr-3 text-blue-600" />
                {isReRequest ? 'Re-request Appointment' : 'Schedule New Appointment'}
              </h1>
              <p className="mt-2 text-gray-600">
                {isReRequest 
                  ? 'Create a new appointment request with the same details as the original appointment'
                  : 'Request an interpreter for a new appointment'
                }
              </p>
            </div>
          </div>
        </motion.div>

        {/* Multi-Step Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow"
        >
          {isReRequest && originalAppointment && (
            <div className="p-6 border-b border-gray-200 bg-green-50">
              <div className="flex items-center">
                <InformationCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                <div>
                  <h3 className="text-sm font-medium text-green-800">
                    Re-requesting appointment from {formatDate(originalAppointment.scheduled_date)}
                  </h3>
                  <p className="text-sm text-green-600 mt-1">
                    The form has been pre-filled with the original appointment details. Please update the date and time for the new appointment.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step Indicator */}
          <div className="p-6 border-b border-gray-200">
            <StepIndicator 
              currentStep={currentStep}
              totalSteps={totalSteps}
              completedSteps={completedSteps}
              onStepClick={goToStep}
            />
          </div>

          {/* Step Content */}
          <div className="p-6">
            <form onSubmit={handleSubmit} onKeyDown={preventEnterSubmit}>
              {currentStep === 1 && (
                <Step1JobType
                  jobType={jobType}
                  setJobType={setJobType}
                  setCurrentStep={setCurrentStep}
                  setServiceType={(value) => handleInputChange('serviceType', value)}
                  showServiceTypeSelection={showServiceTypeSelection}
                  setShowServiceTypeSelection={setShowServiceTypeSelection}
                />
              )}
              
              {currentStep === 2 && (
                <Step2ClaimInfo
                  formData={formData}
                  formOptions={formOptions}
                  allClaims={allClaims}
                  handleInputChange={handleInputChange}
                  handleClaimantChange={handleClaimantChange}
                  handleClaimChange={handleClaimChange}
                  clearClaimantSelection={clearClaimantSelection}
                  onAddClaimClick={openAddClaimModal}
                  onAddClaimantClick={openAddClaimantModal}
                />
              )}
              
              {currentStep === 3 && (
                <Step3AppointmentDetails
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleReserveHoursChange={handleReserveHoursChange}
                  calculateEndTime={calculateEndTime}
                  defaultReserveHours={getDefaultReserveHours(
                    resolveReserveServiceTypeKey(formData, formOptions)
                  )}
                />
              )}
              
              {currentStep === 4 && (
                <Step4InterpreterRequirements
                  formData={formData}
                  formOptions={formOptions}
                  handleInputChange={handleInputChange}
                />
              )}
              
              {currentStep === 5 && (
                <Step5Location
                  formData={formData}
                  formOptions={formOptions}
                  handleInputChange={handleInputChange}
                  handleServiceLocationSelect={handleServiceLocationSelect}
                  locationAutocompleteRef={locationAutocompleteRef}
                  mapsInitialized={mapsInitialized}
                  locationContacts={formData.locationContacts || []}
                  onLocationContactSelect={handleLocationContactSelect}
                  showNewContactModal={showNewContactModal}
                  setShowNewContactModal={setShowNewContactModal}
                  newContactFormData={newContactFormData}
                  setNewContactFormData={setNewContactFormData}
                  handleCreateLocationContact={handleCreateLocationContact}
                  makeAuthenticatedRequest={makeAuthenticatedRequest}
                />
              )}
              
              {currentStep === 6 && (
                <Step6Attachment
                  formData={formData}
                  formOptions={formOptions}
                  allClaims={allClaims}
                  handleInputChange={handleInputChange}
                  calculateEndTime={calculateEndTime}
                  formatDate={formatDate}
                />
              )}
            </form>
          </div>

          {/* Add New Location Contact Modal - Outside form to prevent submission bubbling */}
          {showNewContactModal && (
            <div 
              className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  setShowNewContactModal(false);
                }
              }}
            >
              <div 
                className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="mt-3">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {newContactType === 'location_contact' ? 'Add New Location Contact' : 'Add New Customer'}
                  </h3>
                  
                  {/* Toggle between Customer and Location Contact */}
                  <div className="mb-4 flex gap-2">
                    <button
                      type="button"
                      onClick={() => setNewContactType('customer')}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium ${
                        newContactType === 'customer'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Customer
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewContactType('location_contact')}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium ${
                        newContactType === 'location_contact'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Doctor Name
                    </button>
                  </div>
                  
                  <form onSubmit={handleCreateLocationContact} className="space-y-4">
                    <Input
                      label={newContactType === 'location_contact' ? 'Doctor Name' : 'Name'}
                      value={newContactFormData.name}
                      onChange={(e) => setNewContactFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder={newContactType === 'customer' ? "Enter customer name" : "Doctor name"}
                      required
                    />

                    <Input
                      label={newContactType === 'location_contact' ? 'Email (Optional)' : 'Email'}
                      type="email"
                      value={newContactFormData.email}
                      onChange={(e) => setNewContactFormData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder={newContactType === 'customer' ? "customer@example.com" : "contact@example.com"}
                    />

                    <Input
                      label="Phone"
                      type="tel"
                      value={newContactFormData.phone}
                      onChange={(e) => setNewContactFormData(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="(555) 123-4567"
                    />

                    {newContactType === 'location_contact' && formData.serviceLocationId && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Service Location <span className="text-red-500">*</span>
                        </label>
                        <div className="px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600">
                          {formData.selectedServiceLocation?.name || 'Service Location Selected'}
                        </div>
                      </div>
                    )}

                    <SearchableSelect
                      label="Title"
                      value={newContactFormData.title}
                      onChange={(e) => setNewContactFormData(prev => ({ ...prev, title: e.target.value }))}
                      options={
                        newContactType === 'location_contact' 
                          ? [
                              { value: 'Doctor', label: 'Doctor' },
                              { value: 'Physician Assistant', label: 'Physician Assistant' },
                              { value: 'Nurse Practitioner', label: 'Nurse Practitioner' },
                              { value: 'Nurse', label: 'Nurse' },
                              { value: 'Doctor Assistant', label: 'Doctor Assistant' },
                              { value: 'Receptionist', label: 'Receptionist' },
                              { value: 'Office Coordinator', label: 'Office Coordinator' }
                            ]
                          : [
                              { value: 'Adjuster', label: 'Adjuster' },
                              { value: "Adjuster's Assistant", label: "Adjuster's Assistant" },
                              { value: 'Nurse Case Manager', label: 'Nurse Case Manager' },
                              { value: 'Field Case Manager', label: 'Field Case Manager' },
                              { value: 'Legal Assistant', label: 'Legal Assistant' },
                              { value: 'Claims Supervisor', label: 'Claims Supervisor' }
                            ]
                      }
                      placeholder="Select title (optional)"
                    />

                    <div className="flex justify-end space-x-3 pt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setShowNewContactModal(false);
                          setNewContactType('location_contact');
                          setNewContactFormData({
                            name: '',
                            email: '',
                            phone: '',
                            title: ''
                          });
                        }}
                        className="px-4 py-2 bg-gray-200 text-gray-900 rounded-lg hover:bg-gray-300 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        {newContactType === 'location_contact' ? 'Create Contact' : 'Create Customer'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* Claimant Creation Modal */}
          {showAddClaimantModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4">
              <div className="w-full max-w-2xl rounded-xl bg-white p-6 shadow-2xl">
                <div className="flex items-center justify-between border-b pb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">Add New Claimant</h3>
                    <p className="text-sm text-gray-500">
                      Capture claimant details without leaving this request.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={closeAddClaimantModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form className="mt-4 space-y-4" onSubmit={handleCreateNewClaimant} onKeyDown={preventEnterSubmit}>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Input
                      label="First Name"
                      value={newClaimantData.firstName}
                      onChange={(e) => handleNewClaimantFieldChange('firstName', e.target.value)}
                      onBlur={(e) => {
                        const normalized = capitalizeName(e.target.value);
                        newClaimantNamesRef.current.firstName = normalized;
                        handleNewClaimantFieldChange('firstName', normalized);
                        checkNewClaimantNameOnBlur(normalized, newClaimantNamesRef.current.lastName);
                      }}
                      required
                    />
                    <Input
                      label="Last Name"
                      value={newClaimantData.lastName}
                      onChange={(e) => handleNewClaimantFieldChange('lastName', e.target.value)}
                      onBlur={(e) => {
                        const normalized = capitalizeName(e.target.value);
                        newClaimantNamesRef.current.lastName = normalized;
                        handleNewClaimantFieldChange('lastName', normalized);
                        checkNewClaimantNameOnBlur(newClaimantNamesRef.current.firstName, normalized);
                      }}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Input
                      label="Phone"
                      type="tel"
                      value={newClaimantData.phone}
                      onChange={(e) => handleNewClaimantFieldChange('phone', e.target.value)}
                      placeholder="(555) 123-4567"
                    />
                    <Input
                      label="Date of Birth"
                      type="date"
                      value={newClaimantData.dateOfBirth}
                      onChange={(e) => handleNewClaimantFieldChange('dateOfBirth', e.target.value)}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <SearchableSelect
                        label="Language"
                        value={newClaimantData.language}
                        onChange={(e) => {
                          handleNewClaimantFieldChange('language', e.target.value);
                          // Clear custom language if not "other"
                          if (e.target.value !== 'other') {
                            handleNewClaimantFieldChange('customLanguage', '');
                          }
                        }}
                        options={[
                          ...(formOptions.languages || []).filter(lang => lang.name.toLowerCase() !== 'agency').map(lang => ({
                            value: lang.name,
                            label: lang.name
                          })),
                          { value: 'other', label: 'Other' }
                        ]}
                        placeholder="Select language (optional)"
                      />
                      {newClaimantData.language === 'other' && (
                        <div className="mt-2">
                          <Input
                            label="Enter Language"
                            value={newClaimantData.customLanguage}
                            onChange={(e) => handleNewClaimantFieldChange('customLanguage', e.target.value)}
                            placeholder="Please specify the language"
                          />
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Gender
                      </label>
                      <select
                        value={newClaimantData.gender}
                        onChange={(e) => handleNewClaimantFieldChange('gender', e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      >
                        <option value="">Prefer not to say</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Address (optional)
                    </label>
                    <input
                      type="text"
                      value={newClaimantData.address}
                      onChange={(e) => handleNewClaimantFieldChange('address', e.target.value)}
                      placeholder={mapsInitialized ? "Start typing address for autocomplete..." : "Enter address manually"}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      ref={(input) => {
                        if (input && mapsInitialized && !claimantAddressAutocompleteRef.current) {
                          setupClaimantAddressAutocomplete(input);
                        }
                      }}
                    />
                    {(newClaimantData.address_latitude && newClaimantData.address_longitude) && (
                      <div className="mt-2 text-xs text-gray-500">
                        Coordinates: {newClaimantData.address_latitude}, {newClaimantData.address_longitude}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 border-t pt-4 text-sm text-gray-500 md:flex-row md:items-center md:justify-between">
                    <p>Claimants can be edited later from the Claims area.</p>
                    <div className="flex items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={closeAddClaimantModal}
                        className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                        disabled={isSavingClaimant}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                        disabled={isSavingClaimant}
                      >
                        {isSavingClaimant && (
                          <svg className="mr-2 h-4 w-4 animate-spin" viewBox="0 0 24 24">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                              fill="none"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                            />
                          </svg>
                        )}
                        Create Claimant
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Claim Creation Modal */}
          {showAddClaimModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4">
              <div className="w-full max-w-2xl rounded-xl bg-white p-6 shadow-2xl">
                <div className="flex items-center justify-between border-b pb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">Add New Claim</h3>
                    <p className="text-sm text-gray-500">
                      Create a claim without leaving your appointment request.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={closeAddClaimModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form className="mt-4 space-y-4" onSubmit={handleCreateNewClaim} onKeyDown={preventEnterSubmit}>
                  <SearchableSelect
                    label="Claimant"
                    value={newClaimData.claimantId || formData.claimantId}
                    onChange={(e) => handleNewClaimFieldChange('claimantId', e.target.value)}
                    options={(formOptions.claimants || []).map(claimant => ({
                      value: claimant.id.toString(),
                      label: claimant.first_name && claimant.last_name
                        ? `${claimant.first_name} ${claimant.last_name}`
                        : claimant.name || `Claimant ${claimant.id}`
                    }))}
                    placeholder="Select claimant to associate with this claim"
                    required
                  />

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <SearchableSelect
                      label="Case Type"
                      value={newClaimData.caseType}
                      onChange={(e) => handleNewClaimFieldChange('caseType', e.target.value)}
                      options={caseTypeOptions}
                      placeholder="Select case type"
                      required
                    />

                    <div>
                      <Input
                        label="Claim Number"
                        value={newClaimData.claimNumber}
                        onChange={(e) => handleNewClaimFieldChange('claimNumber', e.target.value)}
                        onBlur={checkNewClaimNumberOnBlur}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Input
                      label="Date of Injury"
                      type="date"
                      value={newClaimData.dateOfInjury}
                      onChange={(e) => handleNewClaimFieldChange('dateOfInjury', e.target.value)}
                    />
                    <Input
                      label="Employer / Insured"
                      value={newClaimData.employer}
                      onChange={(e) => handleNewClaimFieldChange('employer', e.target.value)}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Input
                      label="CT Start Date"
                      type="date"
                      value={newClaimData.ctStartDate}
                      onChange={(e) => handleNewClaimFieldChange('ctStartDate', e.target.value)}
                    />
                    <Input
                      label="CT End Date"
                      type="date"
                      value={newClaimData.ctEndDate}
                      onChange={(e) => handleNewClaimFieldChange('ctEndDate', e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Diagnosis / Notes
                    </label>
                    <textarea
                      value={newClaimData.diagnosis}
                      onChange={(e) => handleNewClaimFieldChange('diagnosis', e.target.value)}
                      rows={3}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      placeholder="Optional context for this claim"
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <SearchableSelect
                      label="Contact/Claims Handler"
                      value={newClaimData.contactClaimsHandlerId}
                      onChange={(e) => handleNewClaimFieldChange('contactClaimsHandlerId', e.target.value)}
                      options={customers.map(c => ({
                        value: c.id.toString(),
                        label: c.name || 'Unnamed Customer'
                      }))}
                      placeholder="Select contact/claims handler..."
                      required
                    />
                    <SearchableSelect
                      label="Adjuster's Assistant"
                      value={newClaimData.adjustersAssistantId}
                      onChange={(e) => handleNewClaimFieldChange('adjustersAssistantId', e.target.value)}
                      options={customers.map(c => ({
                        value: c.id.toString(),
                        label: c.name || 'Unnamed Customer'
                      }))}
                      placeholder="Select adjuster's assistant..."
                      required
                    />
                    <SearchableSelect
                      label="Nurse Case Manager"
                      value={newClaimData.nurseCaseManagerId}
                      onChange={(e) => handleNewClaimFieldChange('nurseCaseManagerId', e.target.value)}
                      options={customers.map(c => ({
                        value: c.id.toString(),
                        label: c.name || 'Unnamed Customer'
                      }))}
                      placeholder="Select nurse case manager..."
                    />
                    <SearchableSelect
                      label="Field Case Manager"
                      value={newClaimData.fieldCaseManagerId}
                      onChange={(e) => handleNewClaimFieldChange('fieldCaseManagerId', e.target.value)}
                      options={customers.map(c => ({
                        value: c.id.toString(),
                        label: c.name || 'Unnamed Customer'
                      }))}
                      placeholder="Select field case manager..."
                    />
                  </div>

                  <div className="flex flex-col gap-2 border-t pt-4 text-sm text-gray-500 md:flex-row md:items-center md:justify-between">
                    <p>Claim details can also be edited later from the Claims tab.</p>
                    <div className="flex items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={closeAddClaimModal}
                        className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                        disabled={isSavingClaim}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                        disabled={isSavingClaim}
                      >
                        {isSavingClaim && (
                          <svg className="mr-2 h-4 w-4 animate-spin" viewBox="0 0 24 24">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                              fill="none"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                            />
                          </svg>
                        )}
                        Create Claim
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center p-6 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={() => navigateBack(navigate, location.pathname, '/appointments')}
              disabled={loading}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              title="Go back to previous page"
            >
              Cancel
            </button>
            
            <div className="flex space-x-4">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={goToPreviousStep}
                  disabled={loading}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 flex items-center"
                >
                  <ArrowLeftIcon className="h-4 w-4 mr-2" />
                  Previous
                </button>
              )}
              
              {currentStep < totalSteps ? (
                isStep1PrimaryAction ? (
                  <button
                    type="button"
                    onClick={handleStep1PrimaryAction}
                    disabled={loading || !jobType}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {step1PrimaryButtonLabel}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={goToNextStep}
                    disabled={loading}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
                  >
                    Next
                    <ArrowRightIcon className="h-4 w-4 ml-2" />
                  </button>
                )
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="h-4 w-4 mr-2" />
                      {isReRequest ? 'Re-request Appointment' : 'Submit Request'}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </div>
      
      {/* Interpreter Search Animation */}
      <InterpreterSearchAnimation 
        isVisible={showSearchAnimation} 
        searchDuration={searchStartTime ? Date.now() - searchStartTime : 0}
        onCancel={handleCancelSearch}
        onClose={handleCloseSearch}
      />

      <DuplicateWarningModal
        isOpen={!!duplicateModal}
        type={duplicateModal?.type}
        data={duplicateModal?.data}
        mode={duplicateModal?.mode}
        onClose={() => {
          duplicateModal?.onResolve?.(false);
          setDuplicateModal(null);
        }}
        onConfirm={() => {
          duplicateModal?.onResolve?.(true);
          setDuplicateModal(null);
        }}
        onViewAppointment={(id) => navigate(`/appointments/${id}`)}
        onViewClaimant={(id) => navigate(`/claimants?claimantId=${id}`)}
        onViewClaim={viewClaim}
      />

      <AdjusterConfirmModal
        isOpen={!!pendingClaim}
        claim={pendingClaim}
        onSave={saveAssignment}
        onSkip={skipPrompt}
        saving={savingRole}
      />
    </div>
  );
};

export default NewAppointment;